import React, { useEffect, useMemo, useState } from "react";
import { Plus, CheckCircle2, XCircle, Receipt, FileText, FileSpreadsheet, DollarSign, Bell, ChevronDown, ChevronRight, Pencil, Trash2, Gift, Search, Download, Archive, RotateCcw } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useSearchParams, useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { cachedGet, prefetch } from "@/lib/cachedGet";
import { TAP_ROW, TAP_ARROW_CELL, TapArrow, TapInfo } from "@/components/TapRow";

const EXPENSE_TYPES = [
  { v: "trip_fee", l: "Trip fee" }, { v: "entry_fee", l: "Entry fee" },
  { v: "costume", l: "Costume" }, { v: "dues", l: "Dues" }, { v: "general", l: "General expense" },
];
const METHODS = [{ v: "cash", l: "Cash" }, { v: "check", l: "Check" }, { v: "credit_card", l: "Credit card" }];
const label = (arr, v) => (arr.find((x) => x.v === v) || {}).l || v;
const expenseLabel = (e) => e?.expense_type_label || label(EXPENSE_TYPES, e?.expense_type);
const money = (n) => { const v = Number(n || 0); return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`; };
const parseItems = (desc) => (desc || "").split("; ").map((p) => {
  const i = p.lastIndexOf(": $");
  return i === -1 ? null : { name: p.slice(0, i), amount: parseFloat(p.slice(i + 3)) || 0 };
}).filter(Boolean);
const expenseItems = (a) => (Array.isArray(a?.items) && a.items.length ? a.items : parseItems(a?.description));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { timeZone: "America/Chicago" }) : "—");
const fmtDay = (s) => {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : fmtDate(s);
};
const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
const toDateInput = (s) => (s ? new Date(s).toLocaleDateString("en-CA", { timeZone: "America/Chicago" }) : todayStr);
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const BOOK_URLS = ["/ledger/general", "/ledger/main-book-summary", "/ledger/summary", "/ledger/payments?status=pending", "/ledger/overview", "/ledger/assignments", "/ledger/fundraisers"];

function Pager({ page, pageCount, onPage, testid }) {
  if (pageCount <= 1) return null;
  return (
    <div className="mt-3 flex items-center justify-between" data-testid={testid}>
      <Button size="sm" variant="outline" className="rounded-full" disabled={page <= 0} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}>Previous</Button>
      <span className="text-xs text-slate-400" data-testid={`${testid}-label`}>Page {page + 1} of {pageCount}</span>
      <Button size="sm" variant="outline" className="rounded-full" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}>Next</Button>
    </div>
  );
}

// Multi-select dropdown of team members (shows ~10 at a time, scrolls beyond) with an
// "Entire Team" shortcut at the top. A single member can also be selected.
function MemberMultiSelect({ members = [], selected = [], setSelected, testid = "member-select", showEntireTeam = true, teamOption = null }) {
  const [open, setOpen] = useState(false);
  const allIds = members.map((m) => m.member_id);
  const teamId = teamOption?.id;
  const teamSel = teamId ? selected.includes(teamId) : false;
  const allSel = allIds.length > 0 && allIds.every((id) => selected.includes(id));
  const toggleAll = () => setSelected(allSel ? [] : [...allIds]);
  const toggleTeam = () => setSelected(teamSel ? [] : [teamId]);
  const toggle = (id) => {
    const base = selected.filter((x) => x !== teamId); // members and team-option are mutually exclusive
    setSelected(base.includes(id) ? base.filter((x) => x !== id) : [...base, id]);
  };
  const selNames = members.filter((m) => selected.includes(m.member_id)).map((m) => m.name);
  const labelText = teamSel ? teamOption.label
    : selected.length === 0 ? "Select team members"
    : allSel ? "Entire Team" : (selNames.length <= 2 ? selNames.join(", ") : `${selNames.length} members selected`);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" className="w-full justify-between rounded-md font-normal" data-testid={`${testid}-trigger`}>
          <span className="truncate text-slate-700">{labelText}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1" data-testid={`${testid}-content`}>
        <div className="max-h-[360px] space-y-0.5 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#cbd5e1_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-transparent">
          {teamOption && (
            <>
              <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50">
                <Checkbox checked={teamSel} onCheckedChange={toggleTeam} data-testid={`${testid}-team`} />
                <span className="text-sm font-semibold text-slate-800">{teamOption.label}</span>
              </label>
              <div className="my-1 border-t border-slate-100" />
            </>
          )}
          {showEntireTeam && (
            <>
              <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50">
                <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid={`${testid}-all`} />
                <span className="text-sm font-semibold text-slate-800">Entire Team</span>
                <span className="ml-auto text-xs text-slate-400">{selNames.length}/{allIds.length}</span>
              </label>
              <div className="my-1 border-t border-slate-100" />
            </>
          )}
          {members.map((m) => (
            <label key={m.member_id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50">
              <Checkbox checked={selected.includes(m.member_id)} onCheckedChange={() => toggle(m.member_id)} data-testid={`${testid}-${m.member_id}`} />
              <span className="text-sm text-slate-700">{m.name}</span>
            </label>
          ))}
          {members.length === 0 && <p className="px-2 py-1.5 text-sm text-slate-400">No active team members.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Ledger() {
  const { user } = useAuth();
  const isCoach = user?.role === "coach";
  return isCoach ? <CoachLedger /> : <MemberLedger />;
}

// When a district/campus admin views a coach's Rally Books (view_only session), hide every
// mutation trigger inside the ledger while keeping read/PDF/CSV/filter/detail controls.
function useReadOnlyLedgerGuard(viewOnly) {
  useEffect(() => {
    if (!viewOnly) return undefined;
    const KEEP_RE = /(pdf|csv|export|filter|tab-|prev|next|page|close|cancel|back|detail|view|search|toggle|start|end|-input|print)/i;
    const TID_RE = /(add|new|create|edit|delete|remove|save|submit|record|approve|reject|deny|allocate|alloc|adjust|refund|waive|assign|bulk|mark|expunge|revoke|pay-|-pay|charge)/i;
    const TXT_RE = /^(add|new|record|edit|delete|remove|save|submit|approve|reject|deny|allocate|adjust|refund|waive|assign|charge|pay|create)\b/i;
    const apply = () => {
      const root = document.querySelector('[data-testid="coach-ledger"]');
      if (!root) return;
      root.querySelectorAll("button").forEach((b) => {
        const tid = b.getAttribute("data-testid") || "";
        if (tid && KEEP_RE.test(tid)) return;
        const txt = (b.textContent || "").trim();
        if ((tid && TID_RE.test(tid)) || TXT_RE.test(txt)) b.style.display = "none";
      });
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [viewOnly]);
}

export function ParentStudentLedger() {
  const { studentId, info } = useOutletContext() || {};
  const name = info ? `${info.first_name} ${info.last_name}` : "Student";
  if (!studentId) return null;
  return (
    <MemberLedger
      readOnly
      dataUrl={`/parent/students/${studentId}/ledger`}
      reportUrl={`/parent/students/${studentId}/ledger/report`}
      title="Rally Books"
      subtitle={`${name}'s balance, payments and receipts (view only)`}
    />
  );
}

function CoachLedger() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const viewOnly = !!user?.view_only;
  const tab = ["general", "member", "fundraising"].includes(params.get("tab")) ? params.get("tab") : "general";
  const setTab = (t) => setParams((p) => { p.set("tab", t); return p; }, { replace: true });
  useReadOnlyLedgerGuard(viewOnly);
  useEffect(() => { prefetch(BOOK_URLS); }, []);
  return (
    <div className="space-y-6" data-testid="coach-ledger">
      <PageHeader title="Rally Books" subtitle={viewOnly ? "Program finances, member accounts and fundraising (view only)" : "Program finances, member accounts and fundraising"} />
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4 grid h-auto w-full grid-cols-3 sm:inline-flex sm:w-auto" data-testid="ledger-tabs">
          <TabsTrigger value="general" className="whitespace-normal px-1.5 text-xs leading-tight sm:whitespace-nowrap sm:px-3 sm:text-sm" data-testid="tab-general">Main Book</TabsTrigger>
          <TabsTrigger value="member" className="whitespace-normal px-1.5 text-xs leading-tight sm:whitespace-nowrap sm:px-3 sm:text-sm" data-testid="tab-member">Team Member Book</TabsTrigger>
          <TabsTrigger value="fundraising" className="whitespace-normal px-1.5 text-xs leading-tight sm:whitespace-nowrap sm:px-3 sm:text-sm" data-testid="tab-fundraising">Fundraising Book</TabsTrigger>
        </TabsList>
        <TabsContent value="general"><MainBook /></TabsContent>
        <TabsContent value="member"><TeamMemberAccount /></TabsContent>
        <TabsContent value="fundraising"><FundraisingAccount /></TabsContent>
      </Tabs>
    </div>
  );
}

function ExportRange({ range, setRange, onPdf, onCsv, testid }) {
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div><Label className="text-xs text-slate-500">Start</Label>
        <Input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="h-9 w-36" data-testid={`${testid}-start`} /></div>
      <div><Label className="text-xs text-slate-500">End</Label>
        <Input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="h-9 w-36" data-testid={`${testid}-end`} /></div>
      <Button variant="outline" size="sm" className="rounded-full" onClick={onPdf} data-testid={`${testid}-pdf-btn`}><FileText className="mr-1 h-3.5 w-3.5" /> PDF</Button>
      <Button variant="outline" size="sm" className="rounded-full" onClick={onCsv} data-testid={`${testid}-csv-btn`}><FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> CSV</Button>
    </div>
  );
}

/* ---------------- Main Book (general ledger sheet + program summary) ---------------- */
function MainBook() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [row, setRow] = useState({ date: todayStr, description: "", category: "expense", amount: "" });
  const [saving, setSaving] = useState(false);
  const [editG, setEditG] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [range, setRange] = useState({ start: "", end: "" });
  const [glPage, setGlPage] = useState(0);
  const [glOpen, setGlOpen] = useState(false);

  const load = (useCache) => {
    cachedGet("/ledger/general", setData, useCache === true).catch(err);
    cachedGet("/ledger/main-book-summary", setSummary, useCache === true).catch(err);
  };
  useEffect(() => { load(true); }, []);

  const submit = async () => {
    if (!row.description.trim()) return toast.error("Enter a description");
    if (row.amount === "" || Number(row.amount) <= 0) return toast.error("Enter a positive amount");
    setSaving(true);
    try {
      await api.post("/ledger/general", { date: row.date || null, description: row.description.trim(), category: row.category, amount: Number(row.amount) });
      toast.success("Entry added");
      setRow({ date: todayStr, description: "", category: "expense", amount: "" });
      setGlOpen(false);
      load();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const removeG = async (g) => {
    if (!window.confirm(`Delete this ${g.category} entry of ${money(g.amount)}?`)) return;
    try { await api.delete(`/ledger/general/${g.id}`); toast.success("Entry deleted"); load(); }
    catch (e) { err(e); }
  };

  const downloadMainBook = async (fmt) => {
    try {
      const res = await api.get(`/ledger/main-book/export?fmt=${fmt}&start=${range.start}&end=${range.end}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `main-book.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Main Book downloaded (${fmt.toUpperCase()})`);
    } catch { toast.error("Unable to download Main Book"); }
  };

  const downloadSummary = async (fmt) => {
    try {
      const res = await api.get(`/ledger/summary/export?fmt=${fmt}`, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `rally-books-summary.${fmt}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Summary downloaded (${fmt.toUpperCase()})`);
    } catch { toast.error("Unable to download summary"); }
  };

  const items = [...(data?.items || [])].sort((a, b) => sortBy === "description"
    ? (a.description || "").localeCompare(b.description || "")
    : (b.created_at || "").localeCompare(a.created_at || ""));
  const GL_PER_PAGE = 5;
  const glPageCount = Math.max(1, Math.ceil(items.length / GL_PER_PAGE));
  const glSafePage = Math.min(glPage, glPageCount - 1);
  const pagedItems = items.slice(glSafePage * GL_PER_PAGE, glSafePage * GL_PER_PAGE + GL_PER_PAGE);
  const combined = summary?.combined_net || 0;
  return (
    <div className="space-y-6" data-testid="main-book">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1" data-testid="main-book-net">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Net (total revenue − total expenses)</p>
          <p className={`font-heading text-2xl font-bold tracking-tight sm:text-3xl ${combined >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="main-book-net-header">{money(combined)}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-full" data-testid="summary-export-btn"><Download className="mr-2 h-4 w-4" /> Summary Export</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" data-testid="summary-export-menu">
            <p className="px-2 py-1.5 text-xs text-slate-500">General Account, Team Member Accounts and fundraising totals — all time.</p>
            <DropdownMenuItem onSelect={() => downloadSummary("pdf")} data-testid="summary-export-pdf"><FileText className="mr-2 h-4 w-4" /> PDF</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => downloadSummary("csv")} data-testid="summary-export-csv"><FileSpreadsheet className="mr-2 h-4 w-4" /> CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Card className="border-slate-200 p-5 shadow-sm" data-testid="main-book-summary">
        <h3 className="mb-4 font-heading text-sm font-semibold text-slate-800">General Account Summary</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between border-b border-slate-50 py-2">
            <span className="text-sm text-slate-600">Team Member Book revenue <span className="text-xs text-slate-400">(payments collected)</span></span>
            <span className="font-semibold tabular-nums text-emerald-600" data-testid="summary-member-revenue">{money(summary?.member_revenue)}</span>
          </div>
          <div className="flex items-center justify-between border-b border-slate-50 py-2">
            <span className="text-sm text-slate-600">Fundraising Book revenue <span className="text-xs text-slate-400">(fundraiser sales)</span></span>
            <span className="font-semibold tabular-nums text-blue-600" data-testid="summary-fundraising-revenue">{money(summary?.fundraising_revenue)}</span>
          </div>
          <div className="flex items-center justify-between border-b border-slate-50 py-2">
            <span className="text-sm text-slate-600">General ledger net <span className="text-xs text-slate-400">(manual entries)</span></span>
            <span className={`font-semibold tabular-nums ${(summary?.general_net || 0) >= 0 ? "text-slate-800" : "text-red-600"}`} data-testid="summary-general-net">{money(summary?.general_net)}</span>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-slate-50/80 px-3 py-3">
            <span className="font-heading text-sm font-semibold text-slate-800">Combined net balance</span>
            <span className={`font-heading text-lg font-bold tabular-nums ${combined >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="summary-combined-net">{money(combined)}</span>
          </div>
        </div>
      </Card>
      <div className="flex flex-wrap justify-end gap-2">
        <Dialog open={glOpen} onOpenChange={setGlOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="gl-add-btn"><Plus className="mr-2 h-4 w-4" /> Add Entry</Button>
          </DialogTrigger>
          <DialogContent data-testid="gl-add-dialog">
            <DialogHeader><DialogTitle>Add general ledger entry</DialogTitle>
              <DialogDescription>Record general team expenses and revenue from other sources such as school budgets and booster club donations.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Date</Label>
                <Input type="date" value={row.date} max={todayStr} onChange={(e) => setRow((s) => ({ ...s, date: e.target.value }))} data-testid="gl-date" /></div>
              <div><Label>Description</Label>
                <Input value={row.description} onChange={(e) => setRow((s) => ({ ...s, description: e.target.value }))} placeholder="e.g. Booster club donation" data-testid="gl-description" /></div>
              <div><Label>Category</Label>
                <Select value={row.category} onValueChange={(v) => setRow((s) => ({ ...s, category: v }))}>
                  <SelectTrigger data-testid="gl-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense" data-testid="gl-category-expense">Expense</SelectItem>
                    <SelectItem value="revenue" data-testid="gl-category-revenue">Revenue</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label>Amount ($)</Label>
                <Input type="number" min="0" step="0.01" value={row.amount} onChange={(e) => setRow((s) => ({ ...s, amount: e.target.value }))} data-testid="gl-amount" /></div>
              <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="gl-submit"><Plus className="mr-2 h-4 w-4" /> Add entry</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="font-heading text-sm font-semibold text-slate-800">General Account Ledger</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-44" data-testid="gl-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date" data-testid="gl-sort-date">Sort by date (newest)</SelectItem>
                <SelectItem value="description" data-testid="gl-sort-description">Sort by description (A–Z)</SelectItem>
              </SelectContent>
            </Select>
            <ExportRange range={range} setRange={setRange} onPdf={() => downloadMainBook("pdf")} onCsv={() => downloadMainBook("csv")} testid="main-book" />
          </div>
        </div>
        {items.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-gl-entries">No general ledger entries yet.</p> : (
          <>
          <div className="space-y-2 sm:hidden" data-testid="gl-cards">
            {pagedItems.map((g) => (
              <RowCard key={g.id} testId={`gl-card-${g.id}`} title={g.description}
                amount={<span className={`font-semibold ${g.category === "revenue" ? "text-emerald-600" : "text-red-600"}`}>{g.category === "revenue" ? "+" : "−"}{money(g.amount)}</span>}
                meta={<span className="flex items-center gap-2">{fmtDate(g.created_at)}
                  <Badge className={`border-0 ${g.category === "revenue" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{g.category === "revenue" ? "Revenue" : "Expense"}</Badge></span>}
                actions={<IconActions onEdit={() => setEditG(g)} onDelete={() => removeG(g)} testId={`gl-card-${g.id}`} />} />
            ))}
            <RowCard testId="gl-net-card" tone={TOTAL_CARD} title="Net (revenue − expense)"
              amount={<span className={`font-semibold ${(data?.net || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(data?.net)}</span>} />
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-sm [&_th]:text-center [&_td]:text-center" data-testid="gl-table">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                <th className="py-2">Date</th><th className="py-2">Description</th><th className="py-2">Category</th>
                <th className="py-2 text-right">Amount</th><th className="py-2 text-right">Edit</th></tr></thead>
              <tbody>
                {pagedItems.map((g) => (
                  <tr key={g.id} className="border-b border-slate-50 even:bg-slate-50/60" data-testid={`gl-row-${g.id}`}>
                    <td className="py-2 text-slate-500">{fmtDate(g.created_at)}</td>
                    <td className="py-2 font-medium text-slate-700">{g.description}</td>
                    <td className="py-2">
                      <Badge className={`border-0 ${g.category === "revenue" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`} data-testid={`gl-cat-${g.id}`}>{g.category === "revenue" ? "Revenue" : "Expense"}</Badge>
                    </td>
                    <td className={`py-2 text-right font-semibold ${g.category === "revenue" ? "text-emerald-600" : "text-red-600"}`} data-testid={`gl-amount-${g.id}`}>{g.category === "revenue" ? "+" : "−"}{money(g.amount)}</td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => setEditG(g)} data-testid={`gl-edit-${g.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => removeG(g)} data-testid={`gl-delete-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t border-slate-200 text-sm font-semibold text-slate-600">
                <td className="py-2" colSpan={3}>Net (revenue − expense)</td>
                <td className={`py-2 text-right ${(data?.net || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="gl-net">{money(data?.net)}</td>
                <td></td></tr></tfoot>
            </table>
          </div>
          <Pager page={glSafePage} pageCount={glPageCount} onPage={setGlPage} testid="gl-pager" />
          </>
        )}
      </Card>

      <EditGeneralDialog entry={editG} onClose={() => setEditG(null)} onSaved={() => { setEditG(null); load(); }} />
    </div>
  );
}

function EditGeneralDialog({ entry, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (entry) setF({ date: toDateInput(entry.created_at), description: entry.description || "", category: entry.category || "expense", amount: String(entry.amount ?? "") });
  }, [entry]);
  if (!entry || !f) return null;
  const submit = async () => {
    if (!f.description.trim()) return toast.error("Enter a description");
    if (f.amount === "" || Number(f.amount) <= 0) return toast.error("Enter a positive amount");
    setSaving(true);
    try {
      await api.put(`/ledger/general/${entry.id}`, { date: f.date || null, description: f.description.trim(), category: f.category, amount: Number(f.amount) });
      toast.success("Entry updated"); onSaved();
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-general-dialog">
        <DialogHeader><DialogTitle>Edit entry</DialogTitle>
          <DialogDescription>Update this general ledger line item.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Date</Label><Input type="date" value={f.date} max={todayStr} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} data-testid="edit-gl-date" /></div>
          <div><Label>Description</Label><Input value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} data-testid="edit-gl-description" /></div>
          <div><Label>Category</Label>
            <Select value={f.category} onValueChange={(v) => setF((s) => ({ ...s, category: v }))}>
              <SelectTrigger data-testid="edit-gl-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="revenue">Revenue</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label>Amount ($)</Label><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} data-testid="edit-gl-amount" /></div>
          <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="edit-gl-submit">Save changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentDetailDialog({ assignment, onClose, onEdit }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!assignment) { setD(null); return; }
    setLoading(true);
    api.get(`/ledger/assignments/${assignment.id}/detail`).then(({ data }) => setD(data)).catch(err).finally(() => setLoading(false));
  }, [assignment]);
  if (!assignment) return null;
  const who = assignment.scope === "group" ? (assignment.subgroup_name || "Group") : "Entire Team";
  const net = d?.net_balance || 0;
  return (
    <Dialog open={!!assignment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="assignment-detail-dialog">
        <DialogHeader><DialogTitle>{expenseLabel(assignment)} · {who}</DialogTitle>
          <DialogDescription>Line items and collection totals for this {assignment.scope === "group" ? "group" : "team"} expense.</DialogDescription></DialogHeader>
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : !d ? null : (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-500">{assignment.plan ? `${assignment.plan_count}-payment plan · ` : ""}{d.member_count} member(s)</p>
              {(() => { const its = expenseItems(assignment); return its.length ? (
                <div className="mt-2" data-testid="assignment-detail-items">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Line items</p>
                  <ul className="mt-1 space-y-1">
                    {its.map((it, i) => <li key={i} className="flex justify-between border-b border-slate-50 py-1 text-sm"><span className="text-slate-600">{it.name}</span><span className="font-medium text-slate-700">{money(it.amount)}</span></li>)}
                  </ul>
                </div>
              ) : (assignment.description ? <p className="mt-1 text-sm text-slate-500">{assignment.description}</p> : null); })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Total expense" value={money(d.total_amount)} tone="text-slate-800" />
              <StatCard label="Payments collected" value={money(d.total_payments)} tone="text-emerald-600" />
              <StatCard label="Fundraising credits applied" value={money(d.fundraising_credits)} tone="text-blue-600" />
              <StatCard label={net > 0 ? "Net balance remaining" : "Fully covered"} value={money(net)} tone={net > 0 ? "text-red-600" : "text-emerald-600"} />
            </div>
            <p className="text-xs text-slate-400" data-testid="assignment-detail-note">Payments and fundraising credits reflect the totals for the {d.member_count} member(s) who received this expense.</p>
            {onEdit && (
              <Button onClick={() => onEdit(assignment)} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="assignment-detail-edit">
                <Pencil className="mr-2 h-4 w-4" /> Edit expense &amp; assigned groups
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LineItemsDialog({ expense, onClose }) {
  const its = expense ? expenseItems(expense) : [];
  const total = its.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const dates = (expense?.plan && Array.isArray(expense?.installment_due_dates)) ? expense.installment_due_dates : [];
  const n = dates.length;
  const perMember = expense?.amount || 0;
  const per = n ? Math.round((perMember / n) * 100) / 100 : 0;
  const schedule = dates.map((d, i) => ({
    label: `Payment ${i + 1} of ${n}`,
    due: d,
    amount: i < n - 1 ? per : Math.round((perMember - per * (n - 1)) * 100) / 100,
  }));
  return (
    <Dialog open={!!expense} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="line-items-dialog">
        <DialogHeader><DialogTitle data-testid="line-items-title">{expense ? expenseLabel(expense) : ""}</DialogTitle>
          <DialogDescription>Full line items for this fee.</DialogDescription></DialogHeader>
        {its.length ? (
          <ul className="space-y-1" data-testid="line-items-list">
            {its.map((it, i) => (
              <li key={i} className="flex justify-between border-b border-slate-50 py-1.5 text-sm" data-testid={`line-item-${i}`}>
                <span className="text-slate-600">{it.name}</span>
                <span className="font-medium text-slate-700 tabular-nums">{money(it.amount)}</span>
              </li>
            ))}
            <li className="flex justify-between pt-2 text-sm font-semibold">
              <span className="text-slate-700">Total</span>
              <span className="text-slate-900 tabular-nums" data-testid="line-items-total">{money(total)}</span>
            </li>
          </ul>
        ) : (
          <p className="text-sm text-slate-500" data-testid="line-items-empty">{expense?.description || "No line items for this fee."}</p>
        )}
        {schedule.length > 0 && (
          <div className="mt-4">
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Installment schedule</h4>
            <table className="w-full text-sm" data-testid="line-items-schedule">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400"><th className="py-1">Payment</th><th className="py-1">Due date</th><th className="py-1 text-right">Amount</th></tr></thead>
              <tbody>
                {schedule.map((s, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`line-items-schedule-row-${i}`}>
                    <td className="py-1.5 text-slate-600">{s.label}</td>
                    <td className="py-1.5 text-slate-600">{s.due ? fmtDay(s.due) : "—"}</td>
                    <td className="py-1.5 text-right font-medium tabular-nums text-slate-700">{money(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function AppliedTo({ amounts, testId }) {
  return (
    <span className="block space-y-0.5" data-testid={testId}>
      {amounts.map((a, i) => (
        <span key={i} className="block" data-testid={`${testId}-${i}`}>{a.label} · <span className={`font-semibold tabular-nums ${a.held ? "text-blue-600" : "text-emerald-600"}`}>{money(a.amount)}</span></span>
      ))}
    </span>
  );
}

function TxDetailsDialog({ tx, onClose }) {
  const isPayment = tx?.kind === "payment";
  const rows = tx ? [
    ["Type", isPayment ? "Payment" : "Credit applied"],
    ["Amount", money(tx.amount)],
    ["Date", fmtDate(tx.date)],
    ["Due date", tx.due_date ? fmtDay(tx.due_date) : "—"],
    ...(isPayment ? [["Method", label(METHODS, tx.method)], ["Status", (tx.status || "approved")], ["Recorded by", tx.source === "coach_receipt" ? "Coach (payment receipt)" : "Member (payment notice)"], ["Receipt #", tx.receipt_no || "—"]] : []),
    ["Applied to", isPayment && tx.applied_amounts?.length ? <AppliedTo amounts={tx.applied_amounts} testId="tx-details-applied-amounts" /> : (tx.applied_to && tx.applied_to !== "—" ? tx.applied_to : "—")],
    ["Note", tx.description || "—"],
  ] : [];
  return (
    <Dialog open={!!tx} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="tx-details-dialog">
        <DialogHeader><DialogTitle data-testid="tx-details-title">{isPayment ? "Payment" : "Credit applied"}</DialogTitle>
          <DialogDescription>Full details for this {isPayment ? "payment" : "applied credit"}.</DialogDescription></DialogHeader>
        <dl className="space-y-1" data-testid="tx-details-list">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-slate-50 py-1.5 text-sm">
              <dt className="text-slate-500">{k}</dt>
              <dd className={`text-right font-medium ${k === "Status" ? "capitalize" : ""} text-slate-700`} data-testid={`tx-details-${k.toLowerCase().replace(/[^a-z]/g, "")}`}>{v}</dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}


function EditAssignmentDialog({ assignment, onClose, onSaved }) {
  const [f, setF] = useState({ description: "", amount: "", due_date: "", date: todayStr });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (assignment) setF({ description: assignment.description || "", amount: String(assignment.amount ?? ""), due_date: assignment.due_date || "", date: toDateInput(assignment.created_at) });
  }, [assignment]);
  if (!assignment) return null;
  const isPlan = !!assignment.plan;
  const save = async () => {
    const body = { description: f.description, date: f.date || null };
    if (!isPlan) {
      if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter an amount");
      body.amount = Number(f.amount);
      body.due_date = f.due_date || null;
    }
    setSaving(true);
    try { await api.put(`/ledger/assignments/${assignment.id}`, body); toast.success("Expense updated"); onSaved(); }
    catch (e) { err(e); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!assignment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-assignment-dialog">
        <DialogHeader><DialogTitle>Edit {assignment.scope === "group" ? "group" : "team"} expense</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!isPlan && <div><Label>Amount per member ($)</Label><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} data-testid="edit-assignment-amount" /></div>}
          {!isPlan && <div><Label>Due date (optional)</Label><Input type="date" value={f.due_date} onChange={(e) => setF((s) => ({ ...s, due_date: e.target.value }))} data-testid="edit-assignment-due-date" /></div>}
          <div><Label>Date of expense</Label><Input type="date" value={f.date} max={todayStr} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} data-testid="edit-assignment-date" /></div>
          <div><Label>Description</Label><Input value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} data-testid="edit-assignment-description" /></div>
          {isPlan && <p className="text-xs text-slate-400">This is a payment plan — only the description can be edited here. Delete and re-create to change amounts or dates.</p>}
          <p className="text-xs text-slate-400">Changes apply to all {assignment.member_count} member account(s).</p>
          <Button onClick={save} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="edit-assignment-save">Save changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Fundraising Account ---------------- */
function FundraiserSelect({ names, value, onChange, testid }) {
  if (names.length === 0) return <p className="text-sm text-slate-400" data-testid={`${testid}-empty`}>Add a fundraiser to the Fundraiser List first.</p>;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Choose a fundraiser" /></SelectTrigger>
      <SelectContent className="max-h-72">{names.map((n, i) => <SelectItem key={n} value={n} data-testid={`${testid}-opt-${i}`}>{n}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function FundraiserListRow({ item: i, archived = false, onToggle }) {
  return (
    <li className="flex items-center justify-between gap-3 py-2" data-testid={`fundraiser-list-item-${i.id}`}>
      <div className="min-w-0">
        <p className={`truncate text-sm ${archived ? "text-slate-400" : "text-slate-700"}`}>{i.name}</p>
        <p className="text-xs text-slate-400" data-testid={`fundraiser-list-totals-${i.id}`}>
          Sales <span className="font-semibold tabular-nums text-blue-600">{money(i.total_sales)}</span>
          {" · "}Credits <span className="font-semibold tabular-nums text-blue-600">{money(i.total_credits)}</span>
          {i.uses > 0 && ` · Used ${i.uses} time${i.uses === 1 ? "" : "s"}`}
        </p>
      </div>
      <Button size="sm" variant="outline" className="shrink-0 rounded-full" onClick={onToggle} data-testid={`fundraiser-list-${archived ? "restore" : "archive"}-${i.id}`}>
        {archived ? <RotateCcw className="mr-1 h-3.5 w-3.5" /> : <Archive className="mr-1 h-3.5 w-3.5" />}{archived ? "Restore" : "Archive"}
      </Button>
    </li>
  );
}

function FundraiserListCard({ items, onChanged }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const active = items.filter((i) => !i.archived);
  const archived = items.filter((i) => i.archived);
  const add = async () => {
    if (!name.trim()) return toast.error("Enter a fundraiser name");
    setSaving(true);
    try { await api.post("/ledger/fundraiser-list", { name: name.trim() }); setName(""); toast.success("Fundraiser added"); onChanged(); }
    catch (e) { err(e); } finally { setSaving(false); }
  };
  const toggle = async (i, value) => {
    try { await api.put(`/ledger/fundraiser-list/${i.id}`, { archived: value }); toast.success(value ? "Fundraiser archived" : "Fundraiser restored"); onChanged(); }
    catch (e) { err(e); }
  };
  return (
    <Card className="border-slate-200 p-5 shadow-sm" data-testid="fundraiser-list-card">
      <span className="mb-3 flex items-center gap-2">
        <TapInfo tip="These fundraisers fill the Add Credits list and your members' Credit Notice list, highest sales first. Totals include Team Fund sales. Archived ones are hidden from both lists but stay on past records." testId="fundraiser-list-info" />
        <h3 className="font-heading text-sm font-semibold text-slate-800">Fundraiser List</h3>
      </span>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input value={name} maxLength={80} placeholder="e.g. Cookie Dough Sale" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid="fundraiser-list-name" />
        <Button onClick={add} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800 sm:w-auto" data-testid="fundraiser-list-add"><Plus className="mr-1 h-4 w-4" /> Add</Button>
      </div>
      {active.length === 0 ? <p className="mt-3 text-sm text-slate-400" data-testid="fundraiser-list-empty">No fundraisers yet. Add one to start giving credits.</p> : (
        <ul className="mt-3 divide-y divide-slate-100" data-testid="fundraiser-list-active">
          {active.map((i) => <FundraiserListRow key={i.id} item={i} onToggle={() => toggle(i, true)} />)}
        </ul>
      )}
      {archived.length > 0 && (
        <>
          <button type="button" onClick={() => setShowArchived((v) => !v)} className="mt-3 flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700" data-testid="fundraiser-list-archived-toggle">
            {showArchived ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Archived ({archived.length})
          </button>
          {showArchived && (
            <ul className="mt-1 divide-y divide-slate-100" data-testid="fundraiser-list-archived">
              {archived.map((i) => <FundraiserListRow key={i.id} item={i} archived onToggle={() => toggle(i, false)} />)}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

function ApproveCreditDialog({ notice, onClose, onDone }) {
  const [f, setF] = useState({ amount: "", sales: "", note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (notice) setF({ amount: String(notice.amount), sales: "", note: "" }); }, [notice]);
  if (!notice) return null;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    const amount = Number(f.amount);
    if (!(amount > 0)) return toast.error("Enter a credit amount");
    if (f.sales !== "" && Number(f.sales) < 0) return toast.error("Sales can't be negative");
    setSaving(true);
    try {
      await api.post(`/ledger/credit-notices/${notice.id}/approve`, { amount, sales: f.sales === "" ? null : Number(f.sales), note: f.note });
      toast.success("Credit approved and logged in the Fundraiser Ledger");
      onDone();
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="approve-credit-dialog">
        <DialogHeader><DialogTitle>Approve credit notice</DialogTitle>
          <DialogDescription>{notice.member_name} · {notice.fundraiser_name}. The credit is added to their account and logged in the Fundraiser Ledger, just like Add Credits.</DialogDescription></DialogHeader>
        <div className="min-w-0 space-y-3">
          {notice.note && <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="approve-credit-member-note">Member's note: {notice.note}</p>}
          <div><Label>Credit ($)</Label>
            <Input type="number" min="0" step="0.01" value={f.amount} onChange={set("amount")} data-testid="approve-credit-amount" />
            {Math.abs((Number(f.amount) || 0) - notice.amount) > 0.005 && <p className="mt-1 text-xs text-amber-600" data-testid="approve-credit-changed">The member sent {money(notice.amount)}. They'll be told the amount you approve.</p>}
          </div>
          <div><Label>Sales ($) <span className="font-normal text-slate-400">(optional)</span></Label>
            <Input type="number" min="0" step="0.01" value={f.sales} placeholder="0.00" onChange={set("sales")} data-testid="approve-credit-sales" /></div>
          <div><Label>Note to member <span className="font-normal text-slate-400">(optional)</span></Label>
            <Input value={f.note} maxLength={200} onChange={set("note")} data-testid="approve-credit-note" /></div>
          <Button onClick={save} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="approve-credit-submit"><CheckCircle2 className="mr-2 h-4 w-4" /> Approve & add credit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreditNoticesCard({ items, onChanged }) {
  const [approve, setApprove] = useState(null);
  const reject = async (n) => {
    if (!window.confirm(`Reject ${n.member_name}'s ${money(n.amount)} credit notice for "${n.fundraiser_name}"?`)) return;
    try { await api.post(`/ledger/credit-notices/${n.id}/reject`, { note: "" }); toast.success("Credit notice rejected"); onChanged(); }
    catch (e) { err(e); }
  };
  return (
    <Card className="border-slate-200 p-5 shadow-sm" data-testid="credit-notices-card">
      <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Pending credit notices</h3>
      {items.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-credit-notices">No credit notices awaiting review.</p> : (
        <div className="space-y-2">
          {items.map((n) => (
            <div key={n.id} className="flex flex-col gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`credit-notice-${n.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-800">{n.member_name} · <span className="text-blue-600">{money(n.amount)}</span></p>
                <p className="text-xs text-slate-400">{n.fundraiser_name} · {fmtDate(n.submitted_at)}{n.note ? ` · ${n.note}` : ""}</p>
              </div>
              <div className="flex shrink-0 gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
                <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => setApprove(n)} data-testid={`credit-notice-approve-${n.id}`}><CheckCircle2 className="mr-1 h-4 w-4" /> Approve</Button>
                <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => reject(n)} data-testid={`credit-notice-reject-${n.id}`}><XCircle className="mr-1 h-4 w-4" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ApproveCreditDialog notice={approve} onClose={() => setApprove(null)} onDone={() => { setApprove(null); onChanged(); }} />
    </Card>
  );
}

function FundraisingAccount() {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [fList, setFList] = useState([]);
  const [notices, setNotices] = useState([]);

  const load = (useCache) => cachedGet("/ledger/fundraisers", setData, useCache === true).catch(err);
  const loadList = () => api.get("/ledger/fundraiser-list").then(({ data }) => setFList(data.items || [])).catch(err);
  const loadNotices = () => api.get("/ledger/credit-notices?status=pending").then(({ data }) => setNotices(data.items || [])).catch(err);
  useEffect(() => {
    load(true);
    loadList();
    loadNotices();
    cachedGet("/ledger/summary", (d) => setMembers(d.rows || [])).catch(() => {});
  }, []);
  const activeNames = fList.filter((i) => !i.archived).map((i) => i.name);

  const [editFr, setEditFr] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [filterMember, setFilterMember] = useState("all");
  const [frPage, setFrPage] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ name: "", sales: "", credit: "", date: todayStr, ids: [], designated_for: "" });
  const [bulkSaving, setBulkSaving] = useState(false);
  const teamFund = bulk.ids.includes("__team__");
  const submitBulk = async () => {
    if (!bulk.name.trim()) return toast.error("Choose a fundraiser");
    if (!bulk.ids.length) return toast.error("Select at least one team member");
    if (bulk.sales === "" || Number(bulk.sales) < 0) return toast.error("Enter a sales amount");
    setBulkSaving(true);
    try {
      if (teamFund) {
        await api.post("/ledger/fundraisers", { name: bulk.name.trim(), member_id: "__team__", sales: Number(bulk.sales) || 0, credit: 0, date: bulk.date || null, designated_for: bulk.designated_for.trim() });
        toast.success("Team fundraiser logged (revenue to Main Book)");
      } else {
        const { data } = await api.post("/ledger/fundraisers/bulk", { name: bulk.name.trim(), member_ids: bulk.ids, sales: Number(bulk.sales) || 0, credit: Number(bulk.credit) || 0, date: bulk.date || null });
        toast.success(`Fundraiser credit created for ${data.created} member(s)`);
      }
      setBulk({ name: "", sales: "", credit: "", date: todayStr, ids: [], designated_for: "" });
      setBulkOpen(false);
      load();
      loadList();
    } catch (e) { err(e); } finally { setBulkSaving(false); }
  };
  const downloadFundraisers = async (fmt) => {
    try {
      const res = await api.get(`/ledger/fundraisers/export?fmt=${fmt}&start=${range.start}&end=${range.end}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `fundraiser-sales-log.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Fundraiser log downloaded (${fmt.toUpperCase()})`);
    } catch { toast.error("Unable to download fundraiser log"); }
  };
  const removeFr = async (f) => {
    if (!window.confirm(`Delete fundraiser "${f.name}"? Its credit of ${money(f.credit)} to ${f.member_name} will be removed too.`)) return;
    try { await api.delete(`/ledger/fundraisers/${f.id}`); toast.success("Fundraiser deleted"); load(); loadList(); }
    catch (e) { err(e); }
  };

  const items = (data?.items || []).filter((f) => filterMember === "all" || (f.member_id || "__team__") === filterMember);
  const FR_PER_PAGE = 5;
  const frPageCount = Math.max(1, Math.ceil(items.length / FR_PER_PAGE));
  const frSafePage = Math.min(frPage, frPageCount - 1);
  const pagedFr = items.slice(frSafePage * FR_PER_PAGE, frSafePage * FR_PER_PAGE + FR_PER_PAGE);
  return (
    <div className="space-y-6" data-testid="fundraising-account">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard label="Total sales (all fundraisers)" value={money(data?.total_sales)} tone="text-blue-600" />
        <StatCard label="Total credits given" value={money(data?.total_credits)} tone="text-blue-600" />
      </div>

      <CreditNoticesCard items={notices} onChanged={() => { loadNotices(); load(); loadList(); }} />

      <FundraiserListCard items={fList} onChanged={loadList} />

      <div className="flex flex-wrap justify-end gap-2">
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full rounded-full border-slate-300 sm:w-auto" data-testid="fundraiser-bulk-btn"><Plus className="mr-2 h-4 w-4" /> Add Credits</Button>
          </DialogTrigger>
          <DialogContent data-testid="fundraiser-bulk-dialog">
            <DialogHeader><DialogTitle>Fundraiser Credit</DialogTitle>
              <DialogDescription>Apply the same fundraiser sales and credit to one or more team members, or log a team fundraiser with no individual credit.</DialogDescription></DialogHeader>
            <div className="min-w-0 space-y-3">
              <div><Label>Fundraiser</Label>
                <FundraiserSelect names={activeNames} value={bulk.name} onChange={(v) => setBulk((s) => ({ ...s, name: v }))} testid="bulk-name" /></div>
              <div><Label>Team members</Label>
                <MemberMultiSelect members={members} selected={bulk.ids} setSelected={(ids) => setBulk((s) => ({ ...s, ids }))} testid="bulk-member" teamOption={{ id: "__team__", label: "Team Fund (No Individual Credit)" }} /></div>
              {teamFund && (
                <div><Label>Towards expense <span className="font-normal text-slate-400">(optional)</span></Label>
                  <Input value={bulk.designated_for} maxLength={120} onChange={(e) => setBulk((s) => ({ ...s, designated_for: e.target.value }))} placeholder="e.g. New competition mats" data-testid="bulk-designated-for" />
                  <p className="mt-1 text-xs text-slate-400">Note the specific team expense this money will go towards.</p></div>
              )}
              <div><Label>Sales ($)</Label>
                <Input type="number" min="0" step="0.01" value={bulk.sales} onChange={(e) => setBulk((s) => ({ ...s, sales: e.target.value }))} data-testid="bulk-sales" /></div>
              <div><Label>Credit ($)</Label>
                <Input type="number" min="0" step="0.01" value={teamFund ? "" : bulk.credit} disabled={teamFund} placeholder={teamFund ? "N/A — goes to Main Book" : ""} onChange={(e) => setBulk((s) => ({ ...s, credit: e.target.value }))} data-testid="bulk-credit" /></div>
              <div><Label>Date</Label>
                <Input type="date" value={bulk.date} max={todayStr} onChange={(e) => setBulk((s) => ({ ...s, date: e.target.value }))} data-testid="bulk-date" /></div>
              <Button onClick={submitBulk} disabled={bulkSaving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="bulk-submit"><Plus className="mr-2 h-4 w-4" /> {teamFund ? "Log team fundraiser" : `Apply to ${bulk.ids.length || 0} member(s)`}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-heading text-sm font-semibold text-slate-800">Fundraiser Ledger</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterMember} onValueChange={setFilterMember}>
              <SelectTrigger className="h-9 w-48" data-testid="fundraiser-filter-member"><SelectValue placeholder="All members" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="fundraiser-filter-all">All members</SelectItem>
                <SelectItem value="__team__" data-testid="fundraiser-filter-team">Team (program)</SelectItem>
                {members.map((m) => <SelectItem key={m.member_id} value={m.member_id} data-testid={`fundraiser-filter-${m.member_id}`}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <ExportRange range={range} setRange={setRange} onPdf={() => downloadFundraisers("pdf")} onCsv={() => downloadFundraisers("csv")} testid="fundraiser-log" />
          </div>
        </div>
        {items.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-fundraisers">No fundraiser sales logged yet.</p> : (
          <>
          <div className="space-y-2 sm:hidden" data-testid="fundraiser-cards">
            {pagedFr.map((f) => (
              <RowCard key={f.id} testId={`fundraiser-card-${f.id}`} title={f.name}
                amount={<Amt label="Credit" tone="text-blue-600">{money(f.credit)}</Amt>}
                meta={<>
                  <p className="font-medium text-slate-600">{f.member_name}</p>
                  {f.designated_for && <p className="text-slate-400">Towards: {f.designated_for}</p>}
                  <p>Sales {money(f.sales)} · {fmtDate(f.created_at)}</p>
                  {f.applied_to && <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600"><CheckCircle2 className="h-3 w-3" /> {f.applied_to}</span>}
                </>}
                actions={<IconActions onEdit={() => setEditFr(f)} onDelete={() => removeFr(f)} testId={`fundraiser-card-${f.id}`} />} />
            ))}
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-sm [&_th]:text-center [&_td]:text-center" data-testid="fundraiser-table">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                <th className="py-2">Fundraiser</th><th className="py-2">Member</th>
                <th className="py-2 text-right">Sales</th><th className="py-2 text-right">Credit</th><th className="py-2 text-right">Date</th><th className="py-2 text-right">Edit</th></tr></thead>
              <tbody>
                {pagedFr.map((f) => (
                  <tr key={f.id} className="border-b border-slate-50 even:bg-slate-50/60" data-testid={`fundraiser-row-${f.id}`}>
                    <td className="py-2 font-medium text-slate-700">{f.name}</td>
                    <td className="py-2 text-slate-600">
                      {f.member_name}
                      {f.designated_for && <span className="mt-0.5 block text-xs text-slate-400" data-testid={`fundraiser-designated-${f.id}`}>Towards: {f.designated_for}</span>}
                    </td>
                    <td className="py-2 text-right text-slate-600">{money(f.sales)}</td>
                    <td className="py-2 text-right text-blue-600">{money(f.credit)}</td>
                    <td className="py-2 text-slate-400">
                      <div className="flex flex-col items-center gap-1">
                        <span>{fmtDate(f.created_at)}</span>
                        {f.applied_to && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600" title={`Applied to ${f.applied_to}`} data-testid={`fundraiser-applied-stamp-${f.id}`}>
                            <CheckCircle2 className="h-3 w-3" /> {f.applied_to}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => setEditFr(f)} data-testid={`fundraiser-edit-${f.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => removeFr(f)} data-testid={`fundraiser-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={frSafePage} pageCount={frPageCount} onPage={setFrPage} testid="fundraiser-pager" />
          </>
        )}
      </Card>
      <EditFundraiserDialog fundraiser={editFr} names={activeNames} onClose={() => setEditFr(null)} onSaved={() => { setEditFr(null); load(); loadList(); }} />
    </div>
  );
}

function EditFundraiserDialog({ fundraiser, names = [], onClose, onSaved }) {
  const [f, setF] = useState({ name: "", sales: "", credit: "", date: todayStr, designated_for: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (fundraiser) setF({ name: fundraiser.name || "", sales: String(fundraiser.sales ?? ""), credit: String(fundraiser.credit ?? ""), date: toDateInput(fundraiser.created_at), designated_for: fundraiser.designated_for || "" });
  }, [fundraiser]);
  if (!fundraiser) return null;
  const isTeam = fundraiser.scope === "team";
  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter a fundraiser name");
    setSaving(true);
    try {
      await api.put(`/ledger/fundraisers/${fundraiser.id}`, { name: f.name.trim(), sales: Number(f.sales) || 0, credit: Number(f.credit) || 0, date: f.date || null, ...(isTeam ? { designated_for: f.designated_for.trim() } : {}) });
      toast.success("Fundraiser updated");
      onSaved();
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!fundraiser} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-fundraiser-dialog">
        <DialogHeader><DialogTitle>Edit fundraiser</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Fundraiser</Label><FundraiserSelect names={[...new Set([fundraiser.name, ...names].filter(Boolean))]} value={f.name} onChange={(v) => setF((s) => ({ ...s, name: v }))} testid="edit-fundraiser-name" /></div>
          {isTeam && <div><Label>Towards expense <span className="font-normal text-slate-400">(optional)</span></Label><Input value={f.designated_for} maxLength={120} placeholder="e.g. New competition mats" onChange={(e) => setF((s) => ({ ...s, designated_for: e.target.value }))} data-testid="edit-fundraiser-designated-for" /></div>}
          <div><Label>Sales ($)</Label><Input type="number" min="0" step="0.01" value={f.sales} onChange={(e) => setF((s) => ({ ...s, sales: e.target.value }))} data-testid="edit-fundraiser-sales" /></div>
          <div><Label>Credit ($)</Label><Input type="number" min="0" step="0.01" value={f.credit} onChange={(e) => setF((s) => ({ ...s, credit: e.target.value }))} data-testid="edit-fundraiser-credit" /></div>
          <div><Label>Date</Label><Input type="date" value={f.date} max={todayStr} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} data-testid="edit-fundraiser-date" /></div>
          <p className="text-xs text-slate-400">Changing the credit updates the credit applied to {fundraiser.member_name}'s account.</p>
          <Button onClick={save} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="edit-fundraiser-save">Save changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Team Member Account ---------------- */
function TeamMemberAccount() {
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [bulkExpOpen, setBulkExpOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [memPage, setMemPage] = useState(0);
  const [filterMember, setFilterMember] = useState("all");
  const [o, setO] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [genExpOpen, setGenExpOpen] = useState(false);
  const [feesOpen, setFeesOpen] = useState(true);
  const [editA, setEditA] = useState(null);
  const [detailA, setDetailA] = useState(null);
  const [feeItems, setFeeItems] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });

  const load = (useCache) => {
    const c = useCache === true;
    cachedGet("/ledger/summary", setSummary, c).catch(err);
    cachedGet("/ledger/payments?status=pending", (d) => setPayments(d.items), c).catch(err);
    cachedGet("/ledger/overview", setO, c).catch(err);
    cachedGet("/ledger/assignments", (d) => setAssignments(d.items || []), c).catch(err);
  };
  useEffect(() => {
    load(true);
    api.get("/coach/subgroups").then(({ data }) => setSubgroups(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
  }, []);

  const removeAssignment = async (a) => {
    const who = a.scope === "group" ? (a.subgroup_name || "group") : "the entire team";
    if (!window.confirm(`Delete this ${expenseLabel(a)} expense assigned to ${who}? It will be removed from all ${a.member_count} member account(s).`)) return;
    try { await api.delete(`/ledger/assignments/${a.id}`); toast.success("Expense deleted"); load(); }
    catch (e) { err(e); }
  };

  const review = async (id, action, note = "") => {
    try {
      await api.post(`/ledger/payments/${id}/${action}`, { note });
      toast.success(`Payment ${action === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (e) { err(e); }
  };

  const downloadTeam = async (fmt) => {
    try {
      const res = await api.get(`/ledger/team/export?fmt=${fmt}&start=${range.start}&end=${range.end}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `team-financial-statement.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Team statement downloaded (${fmt.toUpperCase()})`);
    } catch { toast.error("Unable to download team statement"); }
  };

  const remindMember = async (memberId, name) => {
    try {
      await api.post(`/ledger/member/${memberId}/remind`);
      toast.success(`Reminder sent to ${name}`);
    } catch (e) { err(e); }
  };

  const remindOverdue = async () => {
    try {
      const { data } = await api.post("/ledger/remind-overdue");
      toast.success(data.reminded > 0 ? `Reminded ${data.reminded} overdue member(s)` : "No overdue members to remind");
    } catch (e) { err(e); }
  };

  const members = summary?.rows || [];
  const filteredMembers = filterMember === "all" ? members : members.filter((r) => r.member_id === filterMember);
  const MEM_PER_PAGE = 10;
  const memPageCount = Math.ceil(filteredMembers.length / MEM_PER_PAGE) || 1;
  const memSafePage = Math.min(memPage, memPageCount - 1);
  const pagedMembers = filteredMembers.slice(memSafePage * MEM_PER_PAGE, memSafePage * MEM_PER_PAGE + MEM_PER_PAGE);

  return (
    <div className="space-y-6" data-testid="team-member-account">
      <div className="flex flex-col gap-1" data-testid="tmb-net">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Net Collection of Fees Balance</p>
        <p className={`font-heading text-2xl font-bold tracking-tight sm:text-3xl ${(o?.net_balance || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="tmb-net-header">{money(o?.net_balance)}<span className="ml-2 text-sm font-semibold">{(o?.net_balance || 0) >= 0 ? "(surplus)" : "(deficit)"}</span></p>
      </div>
      <Card className="border-slate-200 p-5 shadow-sm" data-testid="team-member-summary">
        <h3 className="mb-4 font-heading text-sm font-semibold text-slate-800">Team Member Accounts Summary</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between border-b border-slate-50 py-2">
            <span className="text-sm text-slate-600">Total expenses assigned</span>
            <span className="font-semibold tabular-nums text-slate-800" data-testid="tmb-total-expenses">{money(o?.total_expenses)}</span>
          </div>
          <div className="flex items-center justify-between border-b border-slate-50 py-2">
            <span className="text-sm text-slate-600">Total expenses collected</span>
            <span className="font-semibold tabular-nums text-emerald-600" data-testid="tmb-total-collected">{money(o?.total_payments)}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-slate-600">Fundraising credits applied</span>
            <span className="font-semibold tabular-nums text-blue-600" data-testid="tmb-total-credits">{money(o?.total_fundraising_credits)}</span>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 p-5 shadow-sm">
        <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Pending payments</h3>
        {payments.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-pending-payments">No payments awaiting review.</p> : (
          <div className="space-y-2">
            {payments.map((p) => (
              <div key={p.id} className="flex flex-col gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`payment-${p.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{p.member_name} · {money(p.amount)}</p>
                  <p className="text-xs text-slate-400">{label(METHODS, p.method)} · {fmtDate(p.submitted_at)}{p.note ? ` · ${p.note}` : ""}</p>
                </div>
                <div className="flex shrink-0 gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
                  <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => review(p.id, "approve")} data-testid={`approve-${p.id}`}><CheckCircle2 className="mr-1 h-4 w-4" /> Approve</Button>
                  <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => review(p.id, "reject")} data-testid={`reject-${p.id}`}><XCircle className="mr-1 h-4 w-4" /> Reject</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex flex-wrap justify-end gap-2">
        <Dialog open={genExpOpen} onOpenChange={setGenExpOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-full" data-testid="general-add-expense-btn"><Plus className="mr-2 h-4 w-4" /> Team Fees</Button>
          </DialogTrigger>
          <ExpenseForm subgroups={subgroups} mode="general" onDone={() => { setGenExpOpen(false); load(); }} />
        </Dialog>
      </div>

      <Card className="border-slate-200 p-5 shadow-sm">
        <button type="button" onClick={() => setFeesOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 text-left" data-testid="membership-fees-toggle" aria-expanded={feesOpen}>
          <span className="flex items-center gap-2">
            <TapInfo tip="Tap any fee to see its line items." testId="team-fees-tap-info" />
            <h3 className="font-heading text-sm font-semibold text-slate-800">Team Fees</h3>
          </span>
          {feesOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </button>
        {feesOpen && (assignments.length === 0 ? <p className="mt-3 text-sm text-slate-400" data-testid="no-assignments">No team or group expenses yet.</p> : (
          <>
          <div className="mt-3 space-y-2 sm:hidden" data-testid="assignment-cards">
            {assignments.map((a) => (
              <RowCard key={a.id} testId={`assignment-card-${a.id}`} onClick={() => setFeeItems(a)} title={expenseLabel(a)}
                amount={<Amt label="Total">{money(a.total_assigned)}</Amt>}
                meta={<>
                  <p className="font-medium text-slate-600">{a.scope === "group" ? (a.subgroup_name || "Group") : "Entire Team"}</p>
                  <p>{money(a.amount)} per member × {a.member_count}</p>
                  <p>Due {a.due_date ? fmtDay(a.due_date) : (a.plan ? "per plan" : "—")}</p>
                </>}
                actions={<IconActions onEdit={() => setEditA(a)} onDelete={() => removeAssignment(a)} testId={`assignment-card-${a.id}`} />} />
            ))}
            <RowCard testId="team-fees-total-card" tone={TOTAL_CARD} title="Total fees" amount={<span className="font-semibold">{money(assignments.reduce((s, a) => s + (a.total_assigned || 0), 0))}</span>} />
          </div>
          <div className="mt-3 hidden overflow-x-auto sm:block">
            <table className="w-full text-sm [&_th]:text-center [&_td]:text-center" data-testid="assignments-table">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                <th className="py-2">Assigned to</th><th className="py-2">Fee</th>
                <th className="py-2 text-right">Per member</th><th className="py-2 text-right">Members</th>
                <th className="py-2 text-right">Total</th><th className="py-2 text-right">Due date</th><th className="py-2 text-right">Edit</th><th className="sticky right-0 z-10 w-8 bg-white py-2" aria-label="Open"></th></tr></thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} className={`${TAP_ROW} border-b border-slate-100 align-top`} onClick={() => setFeeItems(a)} data-testid={`assignment-row-${a.id}`}>
                    <td className="py-2 font-medium text-slate-700">{a.scope === "group" ? (a.subgroup_name || "Group") : "Entire Team"}</td>
                    <td className="py-2 text-slate-600">
                      <span data-testid={`assignment-name-${a.id}`}>{expenseLabel(a)}</span>
                    </td>
                    <td className="py-2 text-right text-slate-600">{money(a.amount)}</td>
                    <td className="py-2 text-right text-slate-500">{a.member_count}</td>
                    <td className="py-2 text-right font-semibold text-slate-700">{money(a.total_assigned)}</td>
                    <td className="py-2 text-right text-slate-500">{a.due_date ? fmtDay(a.due_date) : (a.plan ? "Plan" : "—")}</td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={(e) => { e.stopPropagation(); setEditA(a); }} data-testid={`assignment-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeAssignment(a); }} data-testid={`assignment-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                    <td className={`py-2 align-middle ${TAP_ARROW_CELL}`}><TapArrow testId={`assignment-open-${a.id}`} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-semibold text-slate-800">
                  <td className="py-2 text-left" colSpan={4}>Total fees</td>
                  <td className="py-2 text-right" data-testid="team-fees-total">{money(assignments.reduce((s, a) => s + (a.total_assigned || 0), 0))}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          </>
        ))}
      </Card>

      <div className="flex flex-wrap justify-end gap-2">
        <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="individual-receipt-btn"><Receipt className="mr-2 h-4 w-4" /> Individual Payment Receipt</Button>
          </DialogTrigger>
          {receiptOpen && <PaymentForm members={members} onDone={() => { setReceiptOpen(false); load(); }} />}
        </Dialog>
        <Dialog open={bulkExpOpen} onOpenChange={setBulkExpOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="add-bulk-expense-btn"><Plus className="mr-2 h-4 w-4" /> Individual/s Fee</Button>
          </DialogTrigger>
          {bulkExpOpen && <ExpenseForm members={members} mode="member-bulk" onDone={() => { setBulkExpOpen(false); load(); }} />}
        </Dialog>
      </div>

      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <TapInfo tip="Tap any member's row to open their account details." testId="member-ledger-tap-info" />
            <h3 className="font-heading text-sm font-semibold text-slate-800">Team Member Ledger</h3>
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterMember} onValueChange={(v) => { setFilterMember(v); setMemPage(0); }}>
              <SelectTrigger className="h-9 w-48" data-testid="ledger-filter-member"><SelectValue placeholder="All members" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="ledger-filter-all">All members</SelectItem>
                {members.map((m) => <SelectItem key={m.member_id} value={m.member_id} data-testid={`ledger-filter-${m.member_id}`}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {summary?.overdue_members > 0 && (
              <Button variant="outline" size="sm" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={remindOverdue} data-testid="remind-overdue-btn"><Bell className="mr-1 h-3.5 w-3.5" /> Remind overdue ({summary.overdue_members})</Button>
            )}
          </div>
        </div>
        <div className="mb-3 flex justify-end">
          <ExportRange range={range} setRange={setRange} onPdf={() => downloadTeam("pdf")} onCsv={() => downloadTeam("csv")} testid="team-statement" />
        </div>
        {members.length === 0 ? <EmptyState title="No members yet" /> : (
          <>
          <div className="space-y-2 sm:hidden" data-testid="summary-cards">
            {pagedMembers.map((r) => (
              <RowCard key={r.member_id} testId={`summary-card-${r.member_id}`} onClick={() => setDetailId(r.member_id)}
                tone={r.overdue ? "border-red-200 bg-red-50" : undefined}
                title={<>{r.name}{r.overdue && <Badge className="ml-2 border-0 bg-red-100 text-red-600">Overdue</Badge>}</>}
                amount={<Amt label="Balance" tone={r.balance > 0 ? "text-red-600" : "text-slate-500"}>{money(r.balance)}</Amt>}
                meta={<>
                  <p>Owed {money(r.total_owed)} · <span className="text-emerald-600">Credit / Paid {money(r.total_paid)}</span></p>
                  <p className={r.overdue ? "font-semibold text-red-600" : ""}>Due {r.next_due ? fmtDay(r.next_due) : "—"}</p>
                </>}
                actions={r.balance > 0.005 && (
                  <Button size="sm" variant="ghost" className={`h-8 rounded-full px-2 ${r.overdue ? "text-red-600 hover:bg-red-100" : "text-slate-500 hover:bg-slate-100"}`} onClick={() => remindMember(r.member_id, r.name)} data-testid={`summary-card-remind-${r.member_id}`}><Bell className="mr-1 h-3.5 w-3.5" /> Remind</Button>
                )} />
            ))}
            <RowCard testId="tml-total-card" tone={TOTAL_CARD} title="Total"
              amount={<Amt label="Balance" tone="text-red-600">{money(filteredMembers.reduce((s, r) => s + (r.balance || 0), 0))}</Amt>}
              meta={<p>Owed {money(filteredMembers.reduce((s, r) => s + (r.total_owed || 0), 0))} · <span className="text-emerald-600">Credit / Paid {money(filteredMembers.reduce((s, r) => s + (r.total_paid || 0), 0))}</span></p>} />
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-sm [&_th:not(:first-child)]:text-center [&_td:not(:first-child)]:text-center" data-testid="ledger-summary-table">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                <th className="py-2 text-left">Member</th><th className="py-2 text-right">Owed</th><th className="py-2 text-right">Credit / Paid</th><th className="py-2 text-right">Balance</th><th className="py-2 text-right">Due date</th><th className="py-2 text-right">Action</th><th className="sticky right-0 z-10 w-8 bg-white py-2" aria-label="Open"></th></tr></thead>
              <tbody>
                {pagedMembers.map((r) => (
                  <tr key={r.member_id} onClick={() => setDetailId(r.member_id)} className={`border-b border-slate-100 ${r.overdue ? "group cursor-pointer bg-red-50 transition-colors hover:bg-red-100" : TAP_ROW}`} data-testid={`summary-row-${r.member_id}`}>
                    <td className="py-2 font-medium">
                      <button type="button" onClick={(e) => { e.stopPropagation(); setDetailId(r.member_id); }}
                        className="text-left text-slate-700 underline decoration-slate-300 underline-offset-2 transition-colors hover:text-orange-600 hover:decoration-orange-400"
                        data-testid={`member-detail-link-${r.member_id}`}>{r.name}</button>
                      {r.overdue && <Badge className="ml-2 border-0 bg-red-100 text-red-600" data-testid={`overdue-badge-${r.member_id}`}>Overdue</Badge>}
                    </td>
                    <td className="py-2 text-right text-slate-600">{money(r.total_owed)}</td>
                    <td className="py-2 text-right text-emerald-600">{money(r.total_paid)}</td>
                    <td className={`py-2 text-right font-semibold ${r.balance > 0 ? "text-red-600" : "text-slate-500"}`}>{money(r.balance)}</td>
                    <td className={`py-2 text-right ${r.overdue ? "font-semibold text-red-600" : "text-slate-500"}`} data-testid={`member-due-${r.member_id}`}>{r.next_due ? fmtDay(r.next_due) : "—"}</td>
                    <td className="py-2 text-right">
                      {r.balance > 0.005 && (
                        <Button size="sm" variant="ghost" className={`h-7 rounded-full px-2 ${r.overdue ? "text-red-600 hover:bg-red-50" : "text-slate-500 hover:bg-slate-100"}`} onClick={(e) => { e.stopPropagation(); remindMember(r.member_id, r.name); }} data-testid={`remind-btn-${r.member_id}`}><Bell className="mr-1 h-3.5 w-3.5" /> Remind</Button>
                      )}
                    </td>
                    <td className={`py-2 ${r.overdue ? "sticky right-0 z-10 w-8 bg-red-50 px-2 group-hover:bg-red-100 shadow-[-1px_0_0_0_#fecaca]" : TAP_ARROW_CELL}`}><TapArrow testId={`summary-open-${r.member_id}`} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-semibold text-slate-800">
                  <td className="py-2 text-left">Total</td>
                  <td className="py-2 text-right text-slate-700" data-testid="tml-total-owed">{money(filteredMembers.reduce((s, r) => s + (r.total_owed || 0), 0))}</td>
                  <td className="py-2 text-right text-emerald-600" data-testid="tml-total-paid">{money(filteredMembers.reduce((s, r) => s + (r.total_paid || 0), 0))}</td>
                  <td className="py-2 text-right text-red-600" data-testid="tml-total-balance">{money(filteredMembers.reduce((s, r) => s + (r.balance || 0), 0))}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Pager page={memSafePage} pageCount={memPageCount} onPage={setMemPage} testid="member-balances-pager" />
          </>
        )}
      </Card>

      <MemberDetailDialog memberId={detailId} onClose={() => setDetailId(null)} onChanged={load} />
      <Dialog open={!!editA} onOpenChange={(o) => { if (!o) setEditA(null); }}>
        {editA && <ExpenseForm editing={editA} subgroups={subgroups} onDone={() => { setEditA(null); load(); }} />}
      </Dialog>
      <AssignmentDetailDialog assignment={detailA} onClose={() => setDetailA(null)} onEdit={(a) => { setDetailA(null); setEditA(a); }} />
      <LineItemsDialog expense={feeItems} onClose={() => setFeeItems(null)} />
    </div>
  );
}

function MemberDetailDialog({ memberId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [editTx, setEditTx] = useState(null);
  const [feeDetail, setFeeDetail] = useState(null);
  const [creditDetail, setCreditDetail] = useState(null);
  const [txDetail, setTxDetail] = useState(null);
  const reload = () => api.get(`/ledger/member/${memberId}`).then(({ data }) => setData(data)).catch(err);
  useEffect(() => {
    if (!memberId) { setData(null); return; }
    setPage(0);
    setSearch("");
    setLoading(true);
    api.get(`/ledger/member/${memberId}`).then(({ data }) => setData(data)).catch(err).finally(() => setLoading(false));
  }, [memberId]);

  const afterChange = () => { reload(); onChanged && onChanged(); };
  const removeTx = async (t) => {
    const isCA = t.kind === "credit_applied";
    const path = t.kind === "payment" ? `/ledger/payments/${t.id}`
      : isCA ? `/ledger/credit-allocations/${t.id}` : `/ledger/entries/${t.id}`;
    const noun = isCA ? "applied credit" : t.kind;
    if (!window.confirm(`Delete this ${noun}? This cannot be undone.`)) return;
    try { await api.delete(path); toast.success("Deleted"); afterChange(); }
    catch (e) { err(e); }
  };

  const txLabel = (t) => {
    if (t.kind === "credit_applied") return "Credit applied";
    if (t.kind === "credit") return t.description ? `Credit — ${t.description}` : "Credit";
    if (t.kind === "payment") return `Payment (${label(METHODS, t.method)})${t.description ? ` — ${t.description}` : ""}`;
    return `${expenseLabel(t)}${t.description ? ` — ${t.description}` : ""}`;
  };
  const txTitle = (t) => {
    if (t.kind === "credit_applied") return `Credit Applied — ${t.expense_label || t.applied_to || "Expense"}`;
    if (t.kind === "payment") {
      const extra = [t.applied_to, t.installment_label].filter((x) => x && x !== "—").join(" — ");
      return `Payment (${label(METHODS, t.method)})${extra ? ` — ${extra}` : ""}`;
    }
    return txLabel(t);
  };
  const signed = (t) => {
    if (t.kind === "expense") return `+${money(t.amount)}`;
    if (t.kind === "credit") return money(t.amount); // created credit — no effect on balance
    if (t.kind === "credit_applied") return `-${money(t.amount)}`;
    if (t.status === "approved") return `-${money(t.amount)}`;
    return money(t.amount); // pending/rejected payment — not applied
  };
  const amountTone = (t) => {
    if (t.kind === "expense") return "text-slate-700";
    if (t.kind === "credit") return "text-slate-900"; // created credit — black, no reduction
    if (t.kind === "credit_applied") return "text-blue-600";
    return t.status === "approved" ? "text-emerald-600" : "text-slate-400 line-through";
  };

  const [range, setRange] = useState({ start: "", end: "" });
  const downloadStatement = async (fmt) => {
    try {
      const res = await api.get(`/ledger/member/${memberId}/export?fmt=${fmt}&start=${range.start}&end=${range.end}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      const safe = (data?.name || "member").replace(/\s+/g, "_");
      a.download = `financial-statement-${safe}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Statement downloaded (${fmt.toUpperCase()})`);
    } catch { toast.error("Unable to download statement"); }
  };

  return (
    <Dialog open={!!memberId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="member-detail-dialog">
        <DialogHeader><DialogTitle>{data?.name || "Member"} — Transaction history</DialogTitle>
          <DialogDescription>Full ledger of charges, credits, applied credits and payments for this member.</DialogDescription></DialogHeader>
        {loading ? <p className="text-sm text-slate-400">Loading…</p> : !data ? null : (
          <div className="space-y-4">
            <div className="flex justify-end">
              <ExportRange range={range} setRange={setRange} onPdf={() => downloadStatement("pdf")} onCsv={() => downloadStatement("csv")} testid="member-detail" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Owed" value={money(data.totals.total_owed)} tone="text-slate-800" />
              <StatCard label="Paid" value={money(data.totals.total_paid)} tone="text-emerald-600" />
              <StatCard label="Balance" value={money(data.totals.balance)} tone={data.totals.balance > 0.005 ? "text-red-600" : "text-emerald-600"} />
            </div>
            {(data.available_credit || 0) > 0.005 && (
              <p className="text-xs text-slate-500" data-testid="member-detail-waiting-credit">{money(data.available_credit)} credit waiting — applied automatically to the next fee added.</p>
            )}
            {data.transactions.length === 0 ? <p className="text-sm text-slate-400" data-testid="member-detail-empty">No transactions yet.</p> : (() => {
              const q = search.trim().toLowerCase();
              const searchText = (t) => [txLabel(t), t.description, t.applied_to, t.receipt_no ? `#${t.receipt_no}` : "",
                t.status, label(METHODS, t.method), money(t.amount), fmtDate(t.date), t.due_date ? fmtDay(t.due_date) : ""]
                .filter(Boolean).join(" ").toLowerCase();
              const filtered = q ? data.transactions.filter((t) => searchText(t).includes(q)) : data.transactions;
              const TX_PER_PAGE = 5;
              const pageCount = Math.ceil(filtered.length / TX_PER_PAGE) || 1;
              const safePage = Math.min(page, pageCount - 1);
              const pageTx = filtered.slice(safePage * TX_PER_PAGE, safePage * TX_PER_PAGE + TX_PER_PAGE);
              const openTx = (t) => { if (t.kind === "expense") setFeeDetail((data.expenses || []).find((x) => x.id === t.id) || null); else if (t.kind === "payment" || t.kind === "credit_applied") setTxDetail(t); };
              const tappable = (t) => t.kind === "expense" || t.kind === "payment" || t.kind === "credit_applied";
              // Fee / payment description; `pre` keeps test ids unique between the phone cards and the table.
              const feeCell = (t, pre = "") => (
                <>
                  {t.kind === "expense"
                    ? <span data-testid={`${pre}tx-fee-link-${t.id}`}>{expenseLabel(t)}</span>
                    : t.kind === "payment"
                    ? <span data-testid={`${pre}tx-detail-link-${t.id}`}>{
                        (t.applied_lines && t.applied_lines.length)
                          ? t.applied_lines.map((l, i) => (
                              <span key={i} className="block">{`Payment (${label(METHODS, t.method)}) — ${l}`}</span>
                            ))
                          : `Payment (${label(METHODS, t.method)})${t.applied_to && t.applied_to !== "—" ? ` — ${t.applied_to}` : ""}`
                      }</span>
                    : t.kind === "credit_applied"
                    ? <span data-testid={`${pre}tx-detail-link-${t.id}`}>{txTitle(t)}</span>
                    : txLabel(t)}
                  {t.kind === "payment" && t.held > 0.005 && (
                    <span className="ml-2 text-xs text-blue-600" data-testid={`${pre}tx-held-${t.id}`}>· {money(t.held)} held as credit</span>
                  )}
                  {t.kind === "payment" && t.status !== "approved" && (
                    <Badge className={`ml-2 border-0 ${t.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{t.status}</Badge>
                  )}
                  {t.kind === "expense" && t.due_date && (() => {
                    const exOverdue = t.due_date < todayStr && (t.remaining ?? t.amount) > 0.005;
                    return (
                    <span className={`ml-2 text-xs ${exOverdue ? "font-semibold text-red-600" : "text-slate-400"}`} data-testid={`${pre}tx-due-${t.id}`}>
                      · Due {fmtDay(t.due_date)}{exOverdue ? " (overdue)" : ""}
                    </span>
                    );
                  })()}
                </>
              );
              return (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    placeholder="Search by type, description, amount, receipt…" className="pl-9"
                    data-testid="member-detail-search" />
                </div>
                {filtered.length === 0 ? (
                  <p className="py-4 text-center text-sm text-slate-400" data-testid="member-detail-no-results">No transactions match "{search}".</p>
                ) : (
                <>
                <div className="space-y-2 sm:hidden" data-testid="member-detail-cards">
                  {pageTx.map((t) => (
                    <RowCard key={`${t.kind}-${t.id}`} testId={`tx-card-${t.id}`} onClick={tappable(t) ? () => openTx(t) : undefined}
                      title={<span className="font-medium text-slate-700">{feeCell(t, "card-")}</span>}
                      amount={<span className={`font-semibold ${amountTone(t)}`}>{signed(t)}</span>}
                      meta={<p>{fmtDate(t.date)} · Balance <span className="font-semibold text-slate-700">{money(t.running_balance)}</span></p>}
                      actions={t.payment_id ? null : <IconActions onEdit={() => setEditTx(t)} onDelete={() => removeTx(t)} testId={`tx-card-${t.id}`} />} />
                  ))}
                </div>
                <div className="hidden overflow-x-auto sm:block">
                <table className="w-full text-sm [&_th]:text-center [&_td]:text-center" data-testid="member-detail-table">
                  <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                    <th className="py-2">Date</th><th className="py-2">Fee</th>
                    <th className="py-2 text-right">Amount</th><th className="py-2 text-right">Balance</th><th className="py-2 text-right">Edit</th></tr></thead>
                  <tbody>
                    {pageTx.map((t) => (
                      <tr key={`${t.kind}-${t.id}`} onClick={() => openTx(t)} className={`border-b border-slate-50 ${tappable(t) ? "cursor-pointer transition-colors hover:bg-slate-50" : ""}`} data-testid={`tx-row-${t.id}`}>
                        <td className="py-2 text-slate-500">{fmtDate(t.date)}</td>
                        <td className="py-2 text-slate-700">{feeCell(t)}</td>
                        <td className={`py-2 text-right font-medium ${amountTone(t)}`}>{signed(t)}</td>
                        <td className="py-2 text-right font-semibold text-slate-700">{money(t.running_balance)}</td>
                        <td className="py-2 text-right">
                          {t.payment_id ? <span className="text-xs text-slate-400" data-testid={`tx-overpay-note-${t.id}`}>From payment</span> : (
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={(e) => { e.stopPropagation(); setEditTx(t); }} data-testid={`tx-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeTx(t); }} data-testid={`tx-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <Pager page={safePage} pageCount={pageCount} onPage={setPage} testid="member-detail-pager" />
                </>
                )}
              </div>
              );
            })()}
          </div>
        )}
      </DialogContent>
      <EditTxDialog tx={editTx} onClose={() => setEditTx(null)} onSaved={() => { setEditTx(null); afterChange(); }} />
      <MemberExpenseDetailDialog expense={feeDetail} onClose={() => setFeeDetail(null)}
        onOpenPayment={(id) => setTxDetail((data?.transactions || []).find((t) => t.kind === "payment" && t.id === id) || null)}
        onOpenCredit={(id) => {
          const c = (data?.transactions || []).find((t) => t.kind === "credit" && t.id === id);
          if (c) setCreditDetail({ amount: c.amount, created_at: c.date, description: c.description, applied_to: c.applied_to });
        }} />
      <MemberCreditDetailDialog credit={creditDetail} onClose={() => setCreditDetail(null)} />
      <TxDetailsDialog tx={txDetail} onClose={() => setTxDetail(null)} />
    </Dialog>
  );
}

function EditTxDialog({ tx, onClose, onSaved }) {
  const [f, setF] = useState({ amount: "", description: "", due_date: "", method: "cash", date: todayStr });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (tx) setF({ amount: String(tx.amount ?? ""), description: tx.description || "",
      due_date: tx.due_date || "", method: tx.method || "cash", date: toDateInput(tx.date) });
  }, [tx]);
  if (!tx) return null;
  const isPayment = tx.kind === "payment";
  const isExpense = tx.kind === "expense";
  const isCreditApplied = tx.kind === "credit_applied";
  const save = async () => {
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter an amount");
    setSaving(true);
    try {
      if (isPayment) {
        await api.put(`/ledger/payments/${tx.id}`, { amount: Number(f.amount), method: f.method, note: f.description, date: f.date || null });
      } else if (isCreditApplied) {
        await api.put(`/ledger/credit-allocations/${tx.id}`, { amount: Number(f.amount), date: f.date || null });
      } else {
        const body = { amount: Number(f.amount), description: f.description, date: f.date || null };
        if (isExpense) body.due_date = f.due_date || null;
        await api.put(`/ledger/entries/${tx.id}`, body);
      }
      toast.success("Updated");
      onSaved();
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!tx} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-tx-dialog">
        <DialogHeader><DialogTitle>Edit {isCreditApplied ? "applied credit" : tx.kind}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isCreditApplied && tx.applied_to && (
            <p className="text-xs text-slate-500" data-testid="edit-tx-applied-to">Applied to: <span className="font-medium text-slate-700">{tx.applied_to}</span></p>
          )}
          <div><Label>Amount ($)</Label><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} data-testid="edit-tx-amount" /></div>
          {isPayment && (
            <div><Label>Method</Label>
              <Select value={f.method} onValueChange={(v) => setF((s) => ({ ...s, method: v }))}>
                <SelectTrigger data-testid="edit-tx-method"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {isExpense && (
            <div><Label>Due date (optional)</Label><Input type="date" value={f.due_date} onChange={(e) => setF((s) => ({ ...s, due_date: e.target.value }))} data-testid="edit-tx-due-date" /></div>
          )}
          <div><Label>{isPayment ? "Date of payment" : isCreditApplied ? "Date of credit" : "Date of transaction"}</Label><Input type="date" value={f.date} max={todayStr} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} data-testid="edit-tx-date" /></div>
          {!isCreditApplied && (
            <div><Label>{isPayment ? "Note" : "Description"}</Label><Input value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} data-testid="edit-tx-description" /></div>
          )}
          <Button onClick={save} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="edit-tx-save">Save changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TOTAL_CARD = "border-slate-200 border-t-2 border-t-slate-300 bg-slate-50/80";

// Phone layout for a coach ledger table row: title + amount on top, details and actions beneath.
function RowCard({ title, amount, meta, actions, onClick, tone = "border-slate-100 bg-white", testId }) {
  return (
    <div role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined} onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
      className={`rounded-xl border p-3 transition-colors ${onClick ? "cursor-pointer active:bg-slate-100" : ""} ${tone}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 text-sm font-semibold text-slate-800">{title}</div>
        <div className="shrink-0 text-right tabular-nums">{amount}</div>
      </div>
      {(meta || actions) && (
        <div className="mt-1.5 flex items-end justify-between gap-3">
          <div className="min-w-0 space-y-0.5 text-xs text-slate-500">{meta}</div>
          {actions && <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>{actions}</div>}
        </div>
      )}
    </div>
  );
}

function IconActions({ onEdit, onDelete, testId }) {
  return (
    <>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-700" onClick={onEdit} data-testid={`${testId}-edit`}><Pencil className="h-3.5 w-3.5" /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={onDelete} data-testid={`${testId}-delete`}><Trash2 className="h-3.5 w-3.5" /></Button>
    </>
  );
}

const Amt = ({ label: l, children, tone = "text-slate-800" }) => (
  <><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}</p><p className={`font-heading text-base font-bold ${tone}`}>{children}</p></>
);

function StatCard({ label, value, tone }) {
  return (
    <Card className="border-slate-200 p-5 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-bold ${tone}`}>{value}</p>
    </Card>
  );
}

function ExpenseForm({ members = [], subgroups = [], mode = "member", editing = null, onDone }) {
  const isEdit = !!editing;
  const isPlanEdit = isEdit && !!editing.plan;
  const [f, setF] = useState(() => isEdit
    ? { member_id: "", subgroup_id: editing.scope === "group" ? editing.subgroup_id : "team", name: editing.expense_type_label || expenseLabel(editing) || "", due_date: editing.due_date || "", date: toDateInput(editing.created_at) }
    : { member_id: "", subgroup_id: "team", name: "", due_date: "", date: todayStr });
  const [items, setItems] = useState(() => {
    const its = isEdit ? expenseItems(editing) : [];
    return its.length ? its.map((i) => ({ name: i.name, amount: String(i.amount) })) : [{ name: "", amount: "" }];
  });
  const TEAM = "__team__";
  const [sel, setSel] = useState(() => {
    if (!isEdit) return [TEAM];
    if (editing.scope === "group") return (editing.subgroup_ids && editing.subgroup_ids.length ? editing.subgroup_ids : (editing.subgroup_id ? [editing.subgroup_id] : []));
    return [TEAM];
  });
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const [plan, setPlan] = useState(false);
  const [planCount, setPlanCount] = useState(() => (isPlanEdit ? (editing.plan_count || 2) : 2));
  const [planDates, setPlanDates] = useState(() => (isPlanEdit ? (editing.installment_due_dates || []).map((d) => d || "") : ["", ""]));
  const [saving, setSaving] = useState(false);

  const total = Math.round(items.reduce((a, r) => a + (Number(r.amount) || 0), 0) * 100) / 100;
  const setItem = (i, k, v) => setItems((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addItem = () => setItems((s) => [...s, { name: "", amount: "" }]);
  const removeItem = (i) => setItems((s) => (s.length > 1 ? s.filter((_, idx) => idx !== i) : s));

  const setCount = (n) => {
    const c = Math.max(2, Math.min(24, Number(n) || 2));
    setPlanCount(c);
    setPlanDates((d) => { const a = d.slice(0, c); while (a.length < c) a.push(""); return a; });
  };
  const splitAmounts = () => {
    const per = Math.round((total / planCount) * 100) / 100;
    return Array.from({ length: planCount }, (_, i) => (i === planCount - 1 ? Math.round((total - per * (planCount - 1)) * 100) / 100 : per));
  };
  const amounts = splitAmounts();

  const [memSel, setMemSel] = useState([]);

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Enter an expense name");
    const cleanItems = items.filter((r) => r.name.trim() && Number(r.amount) > 0);
    const slug = f.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "custom";
    const description = cleanItems.map((r) => `${r.name.trim()}: ${money(Number(r.amount))}`).join("; ");

    if (isEdit) {
      const body = { expense_type: slug, expense_type_label: f.name.trim(), date: f.date || null };
      if (!isPlanEdit) {
        if (cleanItems.length === 0) return toast.error("Add at least one line item with an amount");
        body.description = description;
        body.amount = total;
        body.due_date = f.due_date || null;
      } else {
        if (cleanItems.length === 0) return toast.error("Add at least one line item with an amount");
        body.description = description;
        body.amount = total;
        body.installments = planDates.map((d) => ({ due_date: d || null }));
      }
      if (editing.scope !== "member") {
        const realGroups = sel.filter((x) => x !== TEAM);
        if (sel.includes(TEAM)) { body.scope = "team"; body.subgroup_ids = []; }
        else if (realGroups.length) { body.scope = "group"; body.subgroup_ids = realGroups; }
        else return toast.error("Select Entire Team or at least one group");
      }
      setSaving(true);
      try { await api.put(`/ledger/assignments/${editing.id}`, body); toast.success("Expense updated"); onDone(); }
      catch (e) { err(e); } finally { setSaving(false); }
      return;
    }

    if (cleanItems.length === 0) return toast.error("Add at least one line item with an amount");
    let payload;
    if (mode === "member") {
      if (!f.member_id) return toast.error("Select a team member");
      payload = { scope: "member", member_id: f.member_id };
    } else if (mode === "member-bulk") {
      if (!memSel.length) return toast.error("Select at least one team member");
      payload = { scope: "members", member_ids: memSel };
    } else {
      const realGroups = sel.filter((x) => x !== TEAM);
      if (sel.includes(TEAM)) payload = { scope: "team" };
      else if (realGroups.length) payload = { scope: "group", subgroup_ids: realGroups };
      else return toast.error("Select Entire Team or at least one group");
    }
    payload = { ...payload, expense_type: slug, expense_type_label: f.name.trim(), description, amount: total, date: f.date || null };
    if (plan) {
      if (planDates.some((d) => !d)) return toast.error("Set a due date for every payment");
      payload.installments = planDates.map((d) => ({ due_date: d }));
    } else {
      payload.due_date = f.due_date || null;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/ledger/expenses", payload);
      toast.success(data.plan ? `Payment plan added to ${data.created} member(s)` : `Expense added to ${data.created} member(s)`);
      onDone();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const whoText = editing ? (editing.scope === "group" ? (editing.subgroup_name || "Group") : "Entire Team") : "";

  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
      <DialogHeader><DialogTitle>{isEdit ? "Edit expense" : mode === "member-bulk" ? "Add bulk fee" : "Add expense"}</DialogTitle>
        <DialogDescription>Name the expense, list its line items, and the total is calculated for you.</DialogDescription></DialogHeader>
      <div className="space-y-3">
        {isEdit && editing.scope === "member" ? (
          <div><Label>Assigned to</Label>
            <p className="rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm text-slate-600" data-testid="expense-assigned-to">{whoText}</p></div>
        ) : mode === "member" && !isEdit ? (
          <div><Label>Team member</Label>
            <Select value={f.member_id} onValueChange={(v) => setF((s) => ({ ...s, member_id: v }))}>
              <SelectTrigger data-testid="expense-member"><SelectValue placeholder="Select a member" /></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.member_id} value={m.member_id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : mode === "member-bulk" && !isEdit ? (
          <div><Label>Team members</Label>
            <MemberMultiSelect members={members} selected={memSel} setSelected={setMemSel} testid="expense-member" showEntireTeam={false} />
          </div>
        ) : (
          <div><Label>Assign to</Label>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-100 p-2" data-testid="expense-groups">
              <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="expense-group-team">
                <input type="checkbox" checked={sel.includes(TEAM)} onChange={() => toggleSel(TEAM)} className="h-4 w-4 rounded border-slate-300" data-testid="expense-group-check-team" />
                Entire Team
              </label>
              {subgroups.map((g) => (
                <label key={g.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-slate-700 hover:bg-slate-50" data-testid={`expense-group-${g.id}`}>
                  <input type="checkbox" checked={sel.includes(g.id)} onChange={() => toggleSel(g.id)} className="h-4 w-4 rounded border-slate-300" data-testid={`expense-group-check-${g.id}`} />
                  {g.name}
                </label>
              ))}
            </div>
          </div>
        )}
        <div><Label>Expense</Label>
          <Input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Spring Trip" data-testid="expense-name" /></div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <Label>Line items</Label>
            <Button type="button" size="sm" variant="outline" className="h-7 rounded-full" onClick={addItem} data-testid="expense-item-add"><Plus className="mr-1 h-3.5 w-3.5" /> Add item</Button>
          </div>
          <div className="space-y-2" data-testid="expense-items">
            {items.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`expense-item-row-${i}`}>
                <Input value={r.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Item name" className="flex-1" data-testid={`expense-item-name-${i}`} />
                <Input type="number" min="0" step="0.01" value={r.amount} onChange={(e) => setItem(i, "amount", e.target.value)} placeholder="0.00" className="w-28" data-testid={`expense-item-amount-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-slate-400 hover:text-red-600" onClick={() => removeItem(i)} disabled={items.length <= 1} data-testid={`expense-item-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-2">
          <span className="text-sm font-medium text-slate-600">{(plan || isPlanEdit) ? "Total amount" : "Amount"}</span>
          <span className="font-heading text-lg font-semibold text-slate-800" data-testid="expense-amount">{money(total)}</span>
        </div>

        {!isEdit && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={plan} onChange={(e) => setPlan(e.target.checked)} data-testid="expense-plan-toggle" className="h-4 w-4 rounded border-slate-300" />
            Break into a payment plan
          </label>
        )}

        {!isEdit && plan ? (
          <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid="expense-plan-panel">
            <div><Label>Number of payments</Label>
              <Input type="number" min="2" max="24" value={planCount} onChange={(e) => setCount(e.target.value)} data-testid="expense-plan-count" />
            </div>
            {Array.from({ length: planCount }, (_, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`expense-plan-row-${i}`}>
                <span className="w-24 shrink-0 text-xs text-slate-500">Payment {i + 1} · {money(amounts[i])}</span>
                <Input type="date" value={planDates[i] || ""} onChange={(e) => setPlanDates((d) => { const a = [...d]; a[i] = e.target.value; return a; })} data-testid={`expense-plan-date-${i}`} />
              </div>
            ))}
          </div>
        ) : isPlanEdit ? (
          <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid="expense-plan-panel">
            <Label>Payment due dates</Label>
            {Array.from({ length: planCount }, (_, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`expense-plan-row-${i}`}>
                <span className="w-28 shrink-0 text-xs text-slate-500">Payment {i + 1} · {money(amounts[i])}</span>
                <Input type="date" value={planDates[i] || ""} onChange={(e) => setPlanDates((d) => { const a = [...d]; a[i] = e.target.value; return a; })} data-testid={`expense-plan-date-${i}`} />
              </div>
            ))}
          </div>
        ) : (
          <div><Label>Due date (optional)</Label><Input type="date" value={f.due_date} onChange={(e) => setF((s) => ({ ...s, due_date: e.target.value }))} data-testid="expense-due-date" /></div>
        )}

        <div><Label>Date of expense</Label><Input type="date" value={f.date} max={todayStr} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} data-testid="expense-date" /></div>
        <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="expense-submit">{isEdit ? "Save changes" : "Add expense"}</Button>
      </div>
    </DialogContent>
  );
}

/* ---------------- Member ---------------- */
function DetailRow({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-50 py-1.5 text-sm">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-right font-medium text-slate-700" data-testid={`detail-${k.toLowerCase().replace(/[^a-z]/g, "")}`}>{v}</dd>
    </div>
  );
}

const feeTitle = (e) => `${expenseLabel(e)}${(e.plan_count || 1) > 1 ? ` — Payment ${e.plan_index} of ${e.plan_count}` : ""}`;
const isLaterInstallment = (e) => (e.plan_count || 1) > 1 && (e.plan_index || 1) > 1;

// Issued date, then every date a payment / credit was applied to the fee.
function FeeDates({ e, pre = "" }) {
  const uniq = (ls) => [...new Set((ls || []).map((l) => fmtDate(l.date)))];
  return (
    <div className="mt-0.5 space-y-0.5 text-xs font-normal text-slate-400" data-testid={`${pre}fee-dates-${e.id}`}>
      <p>Issued {fmtDate(e.created_at)}</p>
      {uniq(e.payment_lines).map((d) => <p key={`p${d}`}>Payment applied {d}</p>)}
      {uniq(e.credit_lines).map((d) => <p key={`c${d}`}>Credit applied {d}</p>)}
    </div>
  );
}

// Phone layout for a Member Ledger row: Remaining stands out, the other amounts sit underneath.
function FeeCard({ title, sub, v, remTone, overdue, indent, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testId}
      className={`block rounded-xl border p-3 text-left transition-colors ${indent ? "ml-4 w-[calc(100%-1rem)]" : "w-full"} ${onClick ? "active:bg-slate-100" : "border-t-2 border-t-slate-300 bg-slate-50/80"} ${overdue ? "border-red-200 bg-red-50/50" : "border-slate-100"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800" data-testid={`${testId}-title`}>{title}</p>
          <div className="mt-0.5 text-xs text-slate-400">{sub}</div>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Remaining</p>
          <p className={`font-heading text-lg font-bold tabular-nums ${remTone}`} data-testid={`${testId}-remaining`}>{money(v.remaining)}</p>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2 text-xs">
        {[["Amount", "amount", "text-red-600"], ["Paid", "paid", "text-emerald-600"], ["Credits", "credits_applied", "text-blue-600"]].map(([l, k, tone]) => (
          <div key={k}>
            <p className="text-slate-400">{l}</p>
            <p className={`font-semibold tabular-nums ${tone}`} data-testid={`${testId}-${k}`}>{money(v[k])}</p>
          </div>
        ))}
      </div>
    </Tag>
  );
}

function TxLines({ lines = [], credit = false, onOpen, testId }) {
  if (!lines.length) return null;
  return (
    <ul className="mb-1 space-y-0.5" data-testid={testId}>
      {lines.map((l, i) => (
        <li key={`${l.id}-${i}`}>
          <button type="button" onClick={() => onOpen?.(l.id)} title={credit ? "View credit details" : "View payment receipt"}
            className="group -mx-1 flex w-[calc(100%+0.5rem)] items-start gap-1 rounded px-1 py-0.5 text-left text-xs leading-snug text-slate-500 outline-none transition-colors hover:bg-slate-50 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-300"
            data-testid={`${testId}-${i}`}>
            <span className="min-w-0 flex-1">
            {credit && <span className="text-slate-400">Applied </span>}<span className="tabular-nums">{fmtDate(l.date)}</span> · <span className="font-semibold tabular-nums text-slate-600" data-testid={`${testId}-${i}-applied`}>{money(l.applied ?? l.total)}</span> of <span className="tabular-nums" data-testid={`${testId}-${i}-total`}>{money(l.total)}</span>
              {credit && <span className="block truncate" title={l.source}>{l.source}</span>}
            </span>
            <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function MemberExpenseDetailDialog({ expense, onClose, onOpenPayment, onOpenCredit }) {
  const its = expense ? expenseItems(expense) : [];
  const itemsTotal = its.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const isPlan = (expense?.plan_count || 1) > 1;
  return (
    <Dialog open={!!expense} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="member-expense-detail-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="member-expense-detail-title">{expense ? expenseLabel(expense) : ""}{isPlan ? ` — Payment ${expense?.plan_index} of ${expense?.plan_count}` : ""}</DialogTitle>
          <DialogDescription>{expense?.due_date ? `Due ${fmtDay(expense.due_date)}` : "Full details for this fee."}</DialogDescription>
        </DialogHeader>
        {expense && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {[["Amount", money(expense.amount), "text-slate-800"],
                ["Paid", money(expense.paid), "text-emerald-600", <TxLines lines={expense.payment_lines} onOpen={onOpenPayment} testId="member-expense-payment-lines" />],
                ["Credits applied", money(expense.credits_applied), "text-blue-600", <TxLines lines={expense.credit_lines} credit onOpen={onOpenCredit} testId="member-expense-credit-lines" />],
                ["Remaining", money(expense.remaining), expense.remaining > 0.005 ? "text-red-600" : "text-slate-400"]].map(([lbl, val, tone, extra]) => (
                <div key={lbl} className="rounded-lg border border-slate-100 p-3">
                  <p className="text-xs text-slate-400">{lbl}</p>
                  {extra}
                  <p className={`font-heading text-base font-semibold tabular-nums ${tone}`} data-testid={`member-expense-${lbl.toLowerCase().replace(/[^a-z]/g, "")}`}>{val}</p>
                </div>
              ))}
            </div>
            {its.length > 0 && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Line items</h4>
                <ul className="space-y-1" data-testid="member-expense-line-items">
                  {its.map((it, i) => (
                    <li key={i} className="flex justify-between border-b border-slate-50 py-1.5 text-sm"><span className="text-slate-600">{it.name}</span><span className="font-medium tabular-nums text-slate-700">{money(it.amount)}</span></li>
                  ))}
                  <li className="flex justify-between pt-1 text-sm font-semibold"><span className="text-slate-700">Total</span><span className="tabular-nums text-slate-900">{money(itemsTotal)}</span></li>
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MemberPaymentDetailDialog({ payment, onClose }) {
  const p = payment;
  const rows = p ? [
    ["Amount", money(p.amount)],
    ["Date", fmtDate(p.submitted_at)],
    ["Method", label(METHODS, p.method)],
    ["Status", p.status || "—"],
    ["Recorded by", p.source === "coach_receipt" ? "Coach (payment receipt)" : "Payment notice"],
    ...(p.installment_label ? [["Installment", p.installment_label]] : []),
    ["Applied to", p.applied_amounts?.length ? <AppliedTo amounts={p.applied_amounts} testId="member-payment-applied-amounts" /> : (p.applied_to && p.applied_to !== "—" ? p.applied_to : "—")],
    ["Receipt #", p.receipt?.receipt_no ? `#${p.receipt.receipt_no}` : "—"],
    ["Note", p.note || "—"],
  ] : [];
  return (
    <Dialog open={!!payment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="member-payment-detail-dialog">
        <DialogHeader><DialogTitle>Payment{p?.method ? ` (${label(METHODS, p.method)})` : ""}</DialogTitle>
          <DialogDescription>Full details for this payment.</DialogDescription></DialogHeader>
        <dl className="space-y-1" data-testid="member-payment-detail-list">{rows.map(([k, v]) => <DetailRow key={k} k={k} v={v} />)}</dl>
      </DialogContent>
    </Dialog>
  );
}

function MemberCreditDetailDialog({ credit, onClose }) {
  const c = credit;
  const rows = c ? [
    ["Amount", money(c.amount)],
    ["Received", fmtDate(c.created_at)],
    ["Source", c.description || "Credit"],
    ["Applied to", c.applied_to ? c.applied_to : "Available (not yet applied)"],
  ] : [];
  return (
    <Dialog open={!!credit} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="member-credit-detail-dialog">
        <DialogHeader><DialogTitle>{c?.applied_to ? "Credit applied" : "Credit"}</DialogTitle>
          <DialogDescription>Full details for this credit.</DialogDescription></DialogHeader>
        <dl className="space-y-1" data-testid="member-credit-detail-list">{rows.map(([k, v]) => <DetailRow key={k} k={k} v={v} />)}</dl>
      </DialogContent>
    </Dialog>
  );
}


function SubPageHeader({ title, subtitle, onBack, testId }) {
  return (
    <div className="space-y-3" data-testid={`${testId}-header`}>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-800" data-testid={`${testId}-back`}>
        <ChevronRight className="h-4 w-4 rotate-180" /> Rally Books
      </button>
      <PageHeader title={title} subtitle={subtitle} />
    </div>
  );
}

function LogTable({ headers, rows, total, totalTone, totalNote, totalTestId, testId }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm [&_th]:text-center [&_td]:text-center" data-testid={`${testId}-table`}>
        <thead><tr className="border-b border-slate-100 text-xs text-slate-400">{headers.map((h) => <th key={h} className="py-2 font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-50 even:bg-slate-50/60" data-testid={`${testId}-row-${r.id}`}>
              {r.cells.map((c, i) => <td key={i} className={`py-2 tabular-nums ${c.cls || "text-slate-600"}`}>{c.v}</td>)}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-200 font-semibold">
            <td className="py-2 text-slate-800">Total</td>
            <td className={`py-2 tabular-nums ${totalTone}`} data-testid={totalTestId}>{money(total)}</td>
            <td colSpan={headers.length - 2} className="py-2 text-xs font-normal text-slate-400">{totalNote}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function creditRows(entries) {
  const rows = [];
  entries.filter((e) => e.kind === "credit").forEach((e) => {
    const src = e.fundraiser_name || e.description || "Credit";
    const used = e.used_amount ?? 0;
    const waiting = e.waiting_amount ?? Math.max(0, (e.amount || 0) - used);
    if (used > 0.005) rows.push({ id: `${e.id}-applied`, at: e.applied_at || "", amount: used, src });
    if (waiting > 0.005) rows.push({ id: `${e.id}-waiting`, at: "", amount: waiting, src, waiting: true });
  });
  return rows.sort((a, b) => (b.waiting ? 1 : 0) - (a.waiting ? 1 : 0) || b.at.localeCompare(a.at));
}

function MemberCreditsCard({ data }) {
  const pending = (data?.credit_notices || []).map((n) => ({ id: `notice-${n.id}`, at: "", amount: n.amount, src: n.fundraiser_name, pending: true }));
  const rows = [...pending, ...creditRows(data?.entries || [])];
  const total = rows.filter((r) => !r.pending).reduce((s, r) => s + r.amount, 0);
  const waiting = rows.filter((r) => r.waiting).reduce((s, r) => s + r.amount, 0);
  const pendingTotal = pending.reduce((s, r) => s + r.amount, 0);
  const note = [waiting > 0.005 && `${money(total - waiting)} applied · ${money(waiting)} waiting`,
    pendingTotal > 0.005 && `${money(pendingTotal)} pending approval`].filter(Boolean).join(" · ");
  return (
    <Card className="border-slate-200 p-5 shadow-sm" data-testid="credits-card">
      <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Credits</h3>
      {rows.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-credits">No credits yet.</p> : (
        <LogTable testId="credits" headers={["Date", "Amount", "Source"]} total={total} totalTone="text-blue-600" totalTestId="credits-net-balance"
          totalNote={note}
          rows={rows.map((r) => ({ id: r.id, cells: [
            { v: r.pending ? "Pending approval" : r.waiting ? "Waiting" : (r.at ? fmtDate(r.at) : "—"), cls: r.pending || r.waiting ? "font-medium text-amber-600" : "text-slate-500" },
            { v: money(r.amount), cls: r.pending ? "font-semibold text-slate-400" : "font-semibold text-blue-600" },
            { v: r.src, cls: "text-slate-700" }] }))} />
      )}
    </Card>
  );
}

function MemberPaymentsCard({ data }) {
  const approvedAt = (p) => (p.status === "approved" ? (p.receipt?.date || p.reviewed_at || "") : "");
  const sortKey = (p) => approvedAt(p) || "9999";
  const payments = [...(data?.payments || [])].sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
  const approved = payments.filter((p) => p.status === "approved");
  const total = approved.reduce((s, p) => s + (p.amount || 0), 0);
  const held = approved.reduce((s, p) => s + (p.overpaid || 0), 0);
  return (
    <Card className="border-slate-200 p-5 shadow-sm" data-testid="payments-log-card">
      <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Payment Log</h3>
      {payments.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-payments">No payments submitted.</p> : (
        <LogTable testId="payments" headers={["Date", "Amount", "Type", "Receipt"]} total={total} totalTone="text-emerald-600"
          totalTestId="payments-net-balance" totalNote={held > 0.005 ? `Approved payments · ${money(held)} held as credit` : "Approved payments"}
          rows={payments.map((p) => ({ id: p.id, cells: [
            { v: approvedAt(p) ? fmtDate(approvedAt(p)) : "—", cls: "text-slate-500" },
            { v: money(p.amount), cls: "font-semibold text-emerald-600" },
            { v: label(METHODS, p.method), cls: "text-slate-700" },
            { v: p.receipt?.receipt_no ? `#${p.receipt.receipt_no}` : "—", cls: "text-slate-500" }] }))} />
      )}
    </Card>
  );
}

function SummaryRow({ label: text, value, tone, onClick, testId }) {
  const body = (
    <>
      <span className="text-sm text-slate-600">{text}</span>
      <span className="flex items-center gap-1.5">
        <span className={`font-semibold tabular-nums ${tone}`} data-testid={testId}>{value}</span>
        {onClick && <TapArrow />}
      </span>
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className={`flex w-full items-center justify-between rounded-md border-b border-slate-50 px-1 py-2 text-left ${TAP_ROW}`} data-testid={`${testId}-link`}>{body}</button>
  ) : <div className="flex items-center justify-between border-b border-slate-50 px-1 py-2">{body}</div>;
}

function MemberLedger({ readOnly = false, dataUrl = "/ledger/me", reportUrl = "/ledger/me/report", title = "Rally Books", subtitle = "Your balance, payments and receipts" }) {
  const [data, setData] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });
  const [report, setReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [expPage, setExpPage] = useState(0);
  const [expDetail, setExpDetail] = useState(null);
  const [payDetail, setPayDetail] = useState(null);
  const [creditDetail, setCreditDetail] = useState(null);
  const [params, setParams] = useSearchParams();
  const view = params.get("view");
  const setView = (v) => { setParams((p) => { const n = new URLSearchParams(p); if (v) n.set("view", v); else n.delete("view"); return n; }); window.scrollTo(0, 0); };

  const load = () => api.get(dataUrl).then(({ data }) => setData(data)).catch(err);
  useEffect(() => { load(); }, [dataUrl]);

  const runReport = async () => {
    if (!range.start || !range.end) return toast.error("Pick a start and end date");
    try { const { data } = await api.get(`${reportUrl}?start=${range.start}&end=${range.end}`); setReport(data); }
    catch (e) { err(e); }
  };

  const downloadStatement = async (fmt) => {
    if (!range.start || !range.end) return toast.error("Pick a start and end date");
    try {
      const res = await api.get(`${reportUrl}/export?start=${range.start}&end=${range.end}&fmt=${fmt}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `financial-statement.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Statement downloaded (${fmt.toUpperCase()})`);
    } catch { toast.error("Unable to download statement"); }
  };

  const t = data?.totals || {};
  const dialogs = (
    <>
      <MemberExpenseDetailDialog expense={expDetail} onClose={() => setExpDetail(null)}
        onOpenPayment={(id) => setPayDetail((data?.payments || []).find((p) => p.id === id) || null)}
        onOpenCredit={(id) => setCreditDetail((data?.entries || []).find((c) => c.id === id) || null)} />
      <MemberPaymentDetailDialog payment={payDetail} onClose={() => setPayDetail(null)} />
      <MemberCreditDetailDialog credit={creditDetail} onClose={() => setCreditDetail(null)} />
    </>
  );
  if (view === "credits" || view === "payments") {
    const credits = view === "credits";
    return (
      <div className="space-y-6" data-testid={credits ? "member-credits-page" : "member-payments-page"}>
        <SubPageHeader title={credits ? "Credits" : "Payment Log"} onBack={() => setView(null)} testId={credits ? "credits-page" : "payments-page"}
          subtitle={credits ? "Every credit on the account, when it was applied and where it came from" : "Every payment notice and payment receipt on the account"} />
        {credits ? <MemberCreditsCard data={data} /> : <MemberPaymentsCard data={data} />}
        {dialogs}
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="member-ledger">
      <PageHeader title={title} subtitle={subtitle}
        actions={readOnly ? null : <>
          <Dialog open={payOpen} onOpenChange={setPayOpen}>
            <DialogTrigger asChild><Button className="w-full rounded-full bg-slate-900 hover:bg-slate-800 sm:w-auto" data-testid="submit-payment-btn"><DollarSign className="mr-2 h-4 w-4" /> Submit Payment Notice</Button></DialogTrigger>
            {payOpen && <PaymentForm availableCredit={data?.available_credit || 0} pendingAhead={data?.pending_unapplied || 0} expenses={(data?.expenses || []).filter((e) => e.remaining > 0.005)} onDone={() => { setPayOpen(false); load(); }} />}
          </Dialog>
          <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
            <DialogTrigger asChild><Button className="w-full rounded-full bg-slate-900 hover:bg-slate-800 sm:w-auto" data-testid="submit-credit-btn"><Gift className="mr-2 h-4 w-4" /> Submit Credit Notice</Button></DialogTrigger>
            {creditOpen && <CreditNoticeForm onDone={() => { setCreditOpen(false); load(); }} />}
          </Dialog>
        </>} />

      <div className="flex items-end justify-between gap-3" data-testid="member-net">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Current balance</p>
          <p className={`font-heading text-2xl font-bold tracking-tight sm:text-3xl ${(t.balance || 0) > 0.005 ? "text-red-600" : "text-emerald-600"}`} data-testid="member-current-balance">{money(t.balance)}</p>
        </div>
      </div>

      <Card className="border-slate-200 p-5 shadow-sm" data-testid="member-account-summary">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="font-heading text-sm font-semibold text-slate-800">Account Summary</h3>
          <TapInfo tip="Tap Total paid or Total credits used to open the full log." testId="account-summary-tap-info" />
        </div>
        <div className="space-y-2">
          <SummaryRow label="Total owed" value={money(t.total_owed)} tone="text-red-600" testId="summary-total-owed" />
          <SummaryRow label="Total paid" value={money(t.total_payments)} tone="text-emerald-600" testId="summary-total-paid" onClick={() => setView("payments")} />
          <SummaryRow label="Total credits used" value={money(t.total_credits)} tone="text-blue-600" testId="summary-total-credits-used" onClick={() => setView("credits")} />
          {(data?.available_credit || 0) > 0.005 && (
            <p className="px-1 text-xs text-slate-500" data-testid="summary-waiting-credit">{money(data.available_credit)} credit waiting — it will be applied to the next fee.</p>
          )}
          <div className="flex items-center justify-between rounded-lg bg-slate-50/80 px-3 py-3">
            <span className="font-heading text-sm font-semibold text-slate-800">Current balance</span>
            <span className={`font-heading text-lg font-bold tabular-nums ${(t.balance || 0) > 0.005 ? "text-red-600" : "text-emerald-600"}`} data-testid="summary-current-balance">{money(t.balance)}</span>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="font-heading text-sm font-semibold text-slate-800">Member Ledger</h3>
          <TapInfo tip="Tap any fee for its details." testId="member-ledger-tap-info" />
        </div>
        {(data?.expenses || []).length === 0 ? <p className="text-sm text-slate-400" data-testid="no-assigned-expenses">No assigned expenses yet.</p> : (() => {
          const PER = 5;
          const issued = {};
          data.expenses.forEach((e) => { const k = e.plan_id || e.id; if (!issued[k] || (e.created_at || "") > issued[k]) issued[k] = e.created_at || ""; });
          const sorted = [...data.expenses].sort((a, b) => {
            const ka = a.plan_id || a.id, kb = b.plan_id || b.id;
            if (ka !== kb) return (issued[kb] || "").localeCompare(issued[ka] || "") || kb.localeCompare(ka);
            return (b.plan_index || 0) - (a.plan_index || 0) || (b.due_date || "").localeCompare(a.due_date || "");
          });
          const pages = Math.ceil(sorted.length / PER);
          const page = Math.min(expPage, pages - 1);
          const paged = sorted.slice(page * PER, page * PER + PER);
          return (
          <>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-sm [&_th]:text-center [&_td]:text-center" data-testid="assigned-expenses-table">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                <th className="py-2 !text-left">Fees</th><th className="py-2 text-right">Amount</th>
                <th className="py-2 text-right">Paid</th><th className="py-2 text-right">Credits</th>
                <th className="py-2 text-right">Remaining</th></tr></thead>
              <tbody>
                {paged.map((e) => {
                  const overdue = e.due_date && e.due_date < todayStr && e.remaining > 0.005;
                  return (
                    <tr key={e.id} onClick={() => setExpDetail(e)} className={`cursor-pointer border-b border-slate-50 transition-colors hover:bg-slate-50 ${overdue ? "bg-red-50/50" : "even:bg-slate-50/60"}`} data-testid={`assigned-expense-${e.id}`}>
                      <td className="py-2 !text-left text-slate-700">
                        <div className={isLaterInstallment(e) ? "ml-5 border-l-2 border-slate-200 pl-3" : ""} data-testid={`assigned-expense-fee-${e.id}`}>
                          <span className="font-medium" data-testid={`assigned-expense-title-${e.id}`}>{feeTitle(e)}</span>
                          {overdue && <span className="ml-2 text-xs font-semibold text-red-600">overdue</span>}
                          <FeeDates e={e} />
                        </div></td>
                      <td className="py-2 text-right text-red-600" data-testid={`assigned-expense-amount-${e.id}`}>{money(e.amount)}</td>
                      <td className="py-2 text-right text-emerald-600">{money(e.paid)}</td>
                      <td className="py-2 text-right text-blue-600">{money(e.credits_applied)}</td>
                      <td className={`py-2 text-right font-semibold ${e.remaining > 0.005 ? "text-red-600" : "text-slate-400"}`}>{money(e.remaining)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-semibold" data-testid="member-ledger-totals-row">
                  <td className="py-2 !text-left text-slate-800">Total</td>
                  {[["amount", "text-red-600"], ["paid", "text-emerald-600"], ["credits_applied", "text-blue-600"], ["remaining", "text-slate-400"]].map(([k, tone]) => (
                    <td key={k} className={`py-2 tabular-nums ${tone}`} data-testid={`member-ledger-total-${k}`}>{money(sorted.reduce((s, e) => s + (e[k] || 0), 0))}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="space-y-2 sm:hidden" data-testid="mobile-fee-cards">
            {paged.map((e) => {
              const overdue = e.due_date && e.due_date < todayStr && e.remaining > 0.005;
              return <FeeCard key={e.id} title={<>{feeTitle(e)}{overdue && <span className="ml-2 text-xs font-semibold text-red-600">overdue</span>}</>} sub={<FeeDates e={e} pre="card-" />}
                indent={isLaterInstallment(e)} v={e} remTone={e.remaining > 0.005 ? "text-red-600" : "text-slate-400"} overdue={overdue} onClick={() => setExpDetail(e)} testId={`mobile-fee-card-${e.id}`} />;
            })}
            <FeeCard title="Total" sub={`${sorted.length} fees`} remTone="text-slate-400" testId="mobile-ledger-totals"
              v={Object.fromEntries(["amount", "paid", "credits_applied", "remaining"].map((k) => [k, sorted.reduce((s, e) => s + (e[k] || 0), 0)]))} />
          </div>
            {pages > 1 && (
              <div className="mt-3 flex items-center justify-between" data-testid="member-ledger-pagination">
                <span className="text-xs text-slate-400">Page {page + 1} of {pages} · {sorted.length} entries</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" disabled={page <= 0} onClick={() => setExpPage(page - 1)} data-testid="member-ledger-prev">Previous</Button>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={page >= pages - 1} onClick={() => setExpPage(page + 1)} data-testid="member-ledger-next">Next</Button>
                </div>
              </div>
            )}
          </>
          );
        })()}
      </Card>

      <Card className="border-slate-200 p-5 shadow-sm">
        <button type="button" onClick={() => setReportOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-2 text-left" data-testid="report-toggle">
          <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-800"><FileText className="h-4 w-4" /> Print Financial History Report</h3>          {reportOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </button>
        {reportOpen && (
        <div className="mt-4" data-testid="report-body">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs text-slate-500">Start</Label><Input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} data-testid="report-start" /></div>
          <div><Label className="text-xs text-slate-500">End</Label><Input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} data-testid="report-end" /></div>
          <Button variant="outline" className="rounded-full" onClick={runReport} data-testid="run-report">Generate</Button>
          <Button variant="outline" className="rounded-full" onClick={() => downloadStatement("pdf")} data-testid="statement-pdf-btn"><FileText className="mr-1 h-3.5 w-3.5" /> PDF</Button>
          <Button variant="outline" className="rounded-full" onClick={() => downloadStatement("csv")} data-testid="statement-csv-btn"><FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> CSV</Button>
        </div>
        {report && (
          <div className="mt-4 space-y-5" data-testid="report-result">
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4" data-testid="report-balance-summary">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Current balance</p>
              <p className={`font-heading text-2xl font-bold ${(report.current_balance || 0) > 0.005 ? "text-red-600" : "text-emerald-600"}`} data-testid="report-current-balance">{money(report.current_balance)}</p>
              <p className="mt-1 text-xs text-slate-500">Total owed {money(report.current_owed)} · Total paid {money(report.current_paid)}</p>
            </div>
            <p className="text-sm text-slate-600">
              {fmtDay(report.start)} – {fmtDay(report.end)} · Fees {money(report.total_charges)} · Credits {money(report.total_credits)} · Payments approved {money(report.total_approved)}
            </p>

            <div data-testid="report-charges-section">
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Fees</h4>
              {(report.charges || []).length === 0 ? <p className="text-sm text-slate-400" data-testid="report-charges-empty">No charges in this period.</p> : (
                <div className="overflow-x-auto">
                  <div className="grid min-w-[560px] grid-cols-5 gap-4 border-b border-slate-100 pb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
                    <span>Date</span><span>Type</span><span>Description</span><span className="text-right">Amount</span><span>Receipts</span>
                  </div>
                  {report.charges.map((c) => (
                    <div key={c.id} className="grid min-w-[560px] grid-cols-5 gap-4 border-b border-slate-50 py-2 text-sm text-slate-600" data-testid={`report-charge-${c.id}`}>
                      <span>{c.due_date ? fmtDay(c.due_date) : fmtDate(c.created_at)}</span>
                      <span>{expenseLabel(c)}</span>
                      <span className="truncate" title={c.description || ""}>{c.description || "—"}</span>
                      <span className="text-right">{money(c.amount)}</span>
                      <span className="truncate text-slate-500" title={c.receipts || ""} data-testid={`report-charge-receipts-${c.id}`}>{c.receipts || "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div data-testid="report-credits-section">
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Credits</h4>
              {(report.credits || []).length === 0 ? <p className="text-sm text-slate-400" data-testid="report-credits-empty">No credits in this period.</p> : (
                <div className="overflow-x-auto">
                  <div className="grid min-w-[560px] grid-cols-5 gap-4 border-b border-slate-100 pb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
                    <span>Date</span><span>Source</span><span>Applied to</span><span>Description</span><span className="text-right">Amount</span>
                  </div>
                  {report.credits.map((c, i) => (
                    <div key={i} className="grid min-w-[560px] grid-cols-5 gap-4 border-b border-slate-50 py-2 text-sm text-slate-600" data-testid={`report-credit-${i}`}>
                      <span>{fmtDate(c.created_at)}</span>
                      <span className="truncate" title={c.source || ""} data-testid={`report-credit-source-${i}`}>{c.source || "—"}</span>
                      <span className="truncate" title={c.applied_to || ""}>{c.applied_to || "—"}</span>
                      <span className="truncate" title={c.description || ""}>{c.description || "Credit"}</span>
                      <span className="text-right text-blue-600">-{money(c.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div data-testid="report-payments-section">
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Payments</h4>
              {(report.payments || []).length === 0 ? <p className="text-sm text-slate-400" data-testid="report-payments-empty">No payments in this period.</p> : (
                <div className="overflow-x-auto">
                  <div className="grid min-w-[680px] grid-cols-6 gap-4 border-b border-slate-100 pb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
                    <span>Date</span><span>Payment type</span><span>Applied to</span><span>Receipt #</span><span>Description</span><span className="text-right">Amount</span>
                  </div>
                  {report.payments.map((p) => (
                    <div key={p.id} className="grid min-w-[680px] grid-cols-6 gap-4 border-b border-slate-50 py-2 text-sm text-slate-600" data-testid={`report-row-${p.id}`}>
                      <span>{fmtDate(p.submitted_at)}</span>
                      <span>{label(METHODS, p.method)}</span>
                      <span className="truncate" title={p.applied_to || ""}>{p.applied_to || "—"}</span>
                      <span data-testid={`report-receipt-${p.id}`}>{p.receipt?.receipt_no ? `#${p.receipt.receipt_no}` : "—"}</span>
                      <span className="truncate" data-testid={`report-desc-${p.id}`} title={p.items_desc || p.note || ""}>{p.items_desc || p.note || "—"}</span>
                      <span className="text-right">{money(p.amount)} <em className="text-xs text-slate-400">({p.status})</em></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        </div>
        )}
      </Card>
      {dialogs}
    </div>
  );
}

const cents = (n) => Math.round(n * 100) / 100;

// Splits pending notices (`ahead`) and then `amount` across fees in pay-off order; the rest is held as credit.
function payPlan(fees, amount, ahead = 0) {
  let pend = ahead, left = amount;
  const rows = [...fees].sort((a, b) => (a.pay_order ?? 0) - (b.pay_order ?? 0)).map((e) => {
    let open = Math.max(0, e.open_to_claim ?? e.remaining);
    const pending = cents(Math.min(pend, open)); pend = cents(pend - pending); open = cents(open - pending);
    const pay = cents(Math.min(left, open)); left = cents(left - pay);
    return { fee: e, open, pay, pending };
  });
  return { rows, held: Math.max(0, left) };
}

const centralDay = (s) => (s ? fmtDay(new Date(s).toLocaleDateString("en-CA", { timeZone: "America/Chicago" })) : "—");

function PreviewRow({ fee: e, open, pay, pending }) {
  const full = pay > 0.005 && pay >= open - 0.005;
  const when = e.due_date ? `Due ${fmtDay(e.due_date)}` : `Assigned ${centralDay(e.created_at)}`;
  return (
    <div className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${pay > 0.005 ? "border-emerald-200 bg-emerald-50/60" : "border-slate-100"}`} data-testid={`payment-preview-${e.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-slate-700">{expenseLabel(e)}{e.description ? ` — ${e.description}` : ""}</p>
        <p className="text-xs text-slate-400">{when}{pending > 0.005 && open > 0.005 && <span className="text-amber-600" data-testid={`payment-preview-pending-${e.id}`}> · {money(pending)} in a pending notice</span>}</p>
      </div>
      <div className="shrink-0 text-right text-xs" data-testid={`payment-preview-pay-${e.id}`}>
        {open <= 0.005 ? <span className="text-slate-400">Covered by pending notice</span>
          : pay > 0.005 ? <><span className="block text-sm font-semibold tabular-nums text-emerald-700">Pays {money(pay)}</span><span className="text-slate-400">{full ? "Paid in full" : `of ${money(open)} left`}</span></>
          : <span className="text-slate-400">{money(open)} left</span>}
      </div>
    </div>
  );
}

function PaymentPreview({ fees, amount, ahead, coach }) {
  const { rows, held } = payPlan(fees, amount, ahead);
  return (
    <div className="space-y-2" data-testid="payment-preview">
      <p className="text-xs text-slate-500">Fees are paid in order: earliest due date first. Fees with no due date go by the date they were assigned.{coach ? "" : " Final amounts are set when your coach approves it."}</p>
      {ahead > 0.005 && <p className="text-xs text-amber-600" data-testid="payment-preview-pending">Your pending notices ({money(ahead)}) are counted first.</p>}
      {rows.map((r) => <PreviewRow key={r.fee.id} {...r} />)}
      {held > 0.005 && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2" data-testid="payment-preview-credit">
          <Gift className="h-4 w-4 shrink-0 text-blue-600" />
          <p className="flex-1 text-xs text-blue-700">Held as credit and applied automatically to the next fee.</p>
          <span className="text-sm font-semibold tabular-nums text-blue-700" data-testid="payment-preview-credit-amount">{money(held)}</span>
        </div>
      )}
    </div>
  );
}

function CreditNoticeForm({ onDone }) {
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState({ fundraiser_id: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/ledger/me/fundraisers").then(({ data }) => setOpts(data.items || [])).catch((e) => { setOpts([]); err(e); }); }, []);
  const submit = async () => {
    if (!f.fundraiser_id) return toast.error("Choose a fundraiser");
    const amount = cents(Number(f.amount) || 0);
    if (amount <= 0) return toast.error("Enter the credit amount");
    setSaving(true);
    try {
      await api.post("/ledger/credit-notices", { fundraiser_id: f.fundraiser_id, amount, note: f.note });
      toast.success("Credit notice submitted for review");
      onDone();
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="credit-notice-dialog">
      <DialogHeader>
        <DialogTitle>Submit a Credit Notice</DialogTitle>
        <DialogDescription>Tell your coach about fundraiser credit you've earned. Once your coach approves it, it's applied to your fees automatically.</DialogDescription>
      </DialogHeader>
      <div className="min-w-0 space-y-3">
        <div><Label>Fundraiser</Label>
          {opts === null ? <p className="text-sm text-slate-400">Loading…</p>
            : opts.length === 0 ? <p className="text-sm text-slate-400" data-testid="credit-notice-no-fundraisers">Your coach hasn't added any fundraisers yet.</p> : (
            <Select value={f.fundraiser_id} onValueChange={(v) => setF((s) => ({ ...s, fundraiser_id: v }))}>
              <SelectTrigger data-testid="credit-notice-fundraiser"><SelectValue placeholder="Choose a fundraiser" /></SelectTrigger>
              <SelectContent className="max-h-72">{opts.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`credit-notice-fundraiser-${o.id}`}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <div><Label>Credit amount</Label>
          <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={f.amount} onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} data-testid="credit-notice-amount" /></div>
        <div><Label>Note <span className="font-normal text-slate-400">(optional)</span></Label>
          <Input value={f.note} maxLength={300} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} data-testid="credit-notice-note" /></div>
        <Button onClick={submit} disabled={saving || !opts?.length} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="credit-notice-submit">Submit for approval</Button>
      </div>
    </DialogContent>
  );
}

function PaymentForm({ expenses: memberExpenses = [], availableCredit = 0, pendingAhead = 0, onDone, members = null }) {
  const coach = !!members;
  const [memberId, setMemberId] = useState("");
  const [coachExpenses, setCoachExpenses] = useState([]);
  const [loadingMember, setLoadingMember] = useState(false);
  const expenses = coach ? coachExpenses : memberExpenses;
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(todayStr);
  const [saving, setSaving] = useState(false);
  const total = Math.max(0, cents(Number(amount) || 0));

  const pickMember = (id) => {
    setMemberId(id); setCoachExpenses([]); setLoadingMember(true);
    api.get(`/ledger/member/${id}`)
      .then(({ data }) => setCoachExpenses((data.expenses || []).filter((e) => e.remaining > 0.005)))
      .catch(err).finally(() => setLoadingMember(false));
  };

  const submit = async () => {
    if (coach && !memberId) return toast.error("Choose a team member");
    if (total <= 0) return toast.error("Enter the payment amount");
    if (!date) return toast.error("Pick a payment date");
    setSaving(true);
    try {
      const body = { amount: total, method, note, date };
      if (coach) {
        const { data } = await api.post("/ledger/receipts", { ...body, member_id: memberId });
        toast.success(`Payment applied · Receipt #${data.receipt_no}${data.held_as_credit > 0.005 ? ` · ${money(data.held_as_credit)} held as credit` : ""}`);
      } else {
        await api.post("/ledger/payments", body);
        toast.success("Payment notice submitted for review");
      }
      onDone();
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  const memberName = coach ? (members.find((m) => m.member_id === memberId)?.name || "") : "";
  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid={coach ? "receipt-dialog" : "payment-dialog"}>
      <DialogHeader>
        <DialogTitle>{coach ? "Individual Payment Receipt" : "Submit a Payment Notice"}</DialogTitle>
        <DialogDescription>{coach
          ? "Record a payment you received. It pays the member's fees right away, earliest due date first, and any extra is held as credit. The member and their parents get a receipt notice."
          : "Enter what you paid. Once your coach approves it, it pays your fees earliest due date first, and any extra is held as credit."}</DialogDescription>
      </DialogHeader>
      <div className="min-w-0 space-y-3">
        {coach && (
          <div><Label>Team member</Label>
            <Select value={memberId} onValueChange={pickMember}>
              <SelectTrigger data-testid="receipt-member-select"><SelectValue placeholder="Choose a team member" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {members.map((m) => <SelectItem key={m.member_id} value={m.member_id} data-testid={`receipt-member-${m.member_id}`}>{m.name}{m.balance > 0.005 ? ` · ${money(m.balance)} due` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {!coach && availableCredit > 0.005 && (
          <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="payment-credit-notice">
            <Gift className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-800">You have <span data-testid="payment-credit-amount">{money(availableCredit)}</span> in available credit</p>
              <p className="mt-0.5 text-xs text-emerald-700">It's applied automatically to your next new fee.</p>
            </div>
          </div>
        )}
        <div><Label>Payment amount</Label>
          <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount" />
        </div>
        <div>
          <Label>How it will be applied</Label>
          <div className="mt-1">
            {coach && !memberId ? <p className="text-sm text-slate-400" data-testid="receipt-pick-member-hint">Choose a team member to see their open fees.</p>
              : loadingMember ? <p className="text-sm text-slate-400">Loading fees…</p>
              : (
                <>
                  {expenses.length === 0 && <p className="mb-2 text-sm text-slate-400" data-testid="payment-no-expenses">{coach ? `${memberName} has no open fees.` : "You have no open fees."} The full amount will be held as credit.</p>}
                  <PaymentPreview fees={expenses} amount={total} ahead={coach ? 0 : pendingAhead} coach={coach} />
                </>
              )}
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-slate-900 px-4 py-2 text-white">
          <span className="text-sm">Payment total</span>
          <span className="text-lg font-bold tabular-nums" data-testid="payment-total">{money(total)}</span>
        </div>
        <div><Label>Payment type</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
            <SelectContent>{METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Date of payment</Label><Input type="date" value={date} max={todayStr} onChange={(e) => setDate(e.target.value)} data-testid="payment-date" /></div>
        <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-note" /></div>
        <Button onClick={submit} disabled={saving || (coach && !memberId)} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="payment-submit">{coach ? "Apply payment & send receipt" : "Submit for approval"}</Button>
      </div>
    </DialogContent>
  );
}
